import React, { useState, useEffect, useMemo } from 'react';
import util from '../../utils/util';
import { Modal, Select, Button, Spin, Tag, Row, Col, Progress, Divider, message, Drawer, Checkbox, Input, Empty } from 'antd';
import { PrinterOutlined, FileExcelOutlined, FilePdfOutlined, SettingOutlined, SearchOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';
import moment from 'moment';
import coa  from '../assets/footer/coa.png';
import flag from '../assets/footer/flag.png';


const getFacScore = (f) => { const m = localStorage.getItem('qoc_scoring_mode') || 'benchmark'; return parseFloat((m === 'benchmark' ? (f.overall_benchmark_score || f.overall_percentage) : f.overall_percentage) || 0); };
const { Option } = Select;

const CLAUSES = [
    { code:'AACC', title:'Access, Assessment and Continuity of Care',           color:'#0ea5e9', num:1 },
    { code:'COP',  title:'Care of the Patient',                                  color:'#8b5cf6', num:2 },
    { code:'MOM',  title:'Management of Medication',                             color:'#f59e0b', num:3 },
    { code:'PRPE', title:'Patient Rights and Patient Education',                  color:'#10b981', num:4 },
    { code:'IPCM', title:'Infection Prevention and Control Management',           color:'#ef4444', num:5 },
    { code:'PSQI', title:'Patient Safety and Quality Improvement',               color:'#3b82f6', num:6 },
    { code:'GROM', title:'Governance, Roles and Responsibilities of Management', color:'#6366f1', num:7 },
    { code:'FMS',  title:'Facility Management, Safety and Support Services',     color:'#ec4899', num:8 },
    { code:'HRE',  title:'Human Resource and Education',                         color:'#14b8a6', num:9 },
    { code:'ISM',  title:'Information System Management',                        color:'#a855f7', num:10 },
];

const RATING = [
    { min:98,  max:100,  label:'Outstanding',          stars:5, color:'#1565C0', rowBg:'#1565C0', rowText:'#fff' },
    { min:95,  max:97.9, label:'Excellent',            stars:4, color:'#2E7D32', rowBg:'#2E7D32', rowText:'#fff' },
    { min:90,  max:94.9, label:'Good',                 stars:3, color:'#558B2F', rowBg:'#558B2F', rowText:'#fff' },
    { min:85,  max:89.9, label:'Fair',                 stars:2, color:'#FFFF00', rowBg:'#FFFF00', rowText:'#000' },
    { min:70,  max:84.9, label:'Requires Improvement', stars:1, color:'#E65100', rowBg:'#E65100', rowText:'#fff' },
    { min:0,   max:69.9, label:'Inadequate',           stars:0, color:'#B71C1C', rowBg:'#B71C1C', rowText:'#fff' },
];
const getRating = s => RATING.find(r => s >= r.min && s <= r.max) || RATING[RATING.length-1];
const avg       = arr => arr.length ? arr.reduce((a,b)=>a+b,0)/arr.length : 0;
const safeColor = c => c === '#FFFF00' ? '#000' : c;  // yellow is invisible as text

const REGION_LIST   = ['Hhohho','Manzini','Lubombo','Shiselweni'];
const REGION_COLORS = { Hhohho:'#1565C0', Manzini:'#B71C1C', Lubombo:'#4A148C', Shiselweni:'#4E342E' };

// ── Customizable report sections ──────────────────────────────────────────────
// Master list of every toggleable section. `appliesTo` controls which report
// type shows that checkbox in the customize panel — 'both' sections exist in
// both National and Regional reports (though the two sometimes render them
// differently), 'national'/'regional' are exclusive to one report type.
const SECTION_DEFS = [
    { id:'coverPage',                label:'Cover Page',                                appliesTo:'both' },
    { id:'toc',                      label:'Table of Contents',                         appliesTo:'both' },
    { id:'executiveSummary',         label:'Executive Summary',                         appliesTo:'both' },
    { id:'starRating',               label:'Star Rating Distribution',                  appliesTo:'both' },
    { id:'nationalAggregates',       label:'National Aggregate Cards (Clauses & QI)',   appliesTo:'national' },
    { id:'regionalMap',              label:'Regional Performance Map',                  appliesTo:'national' },
    { id:'findingsByType',           label:'Findings by Facility Type',                 appliesTo:'national' },
    { id:'findingsDiscussions',      label:'Findings & Discussions',                    appliesTo:'regional' },
    { id:'regionalDeepDive',         label:'Regional Deep-Dive (per region)',           appliesTo:'national' },
    { id:'qualityIndex',             label:'Quality Index',                             appliesTo:'both' },
    { id:'facilityDetail',           label:'Facility Detail (summary table)',           appliesTo:'both' },
    { id:'heatmap',                  label:'Performance Heatmap',                       appliesTo:'both' },
    { id:'clausePerformanceSummary', label:'Clause Performance Summary',                appliesTo:'both' },
    { id:'periodComparison',         label:'Period Comparison',                         appliesTo:'both' },
    { id:'qualitativeComments',      label:'Auditor Comments & Qualitative Findings',   appliesTo:'both' },
    { id:'clauseByClauseSummary',    label:'Clause-by-Clause Summary (detailed)',       appliesTo:'regional' },
    { id:'recommendations',          label:'Recommendations & Conclusion',              appliesTo:'both' },
];

// ── Shared "UN SDG report style" presentation helpers ───────────────────────
// A small set of reusable pieces modelled on The Sustainable Development
// Goals Report 2025's own house style: every major section leads with 3-4
// plain-language bullets before any narrative prose or chart, charts/tables
// carry a small italic "Note:" caption for methodology/caveats, and a
// consistent "Source:" attribution line identifies where the numbers came
// from. Centralising these means every section that adopts them reads and
// looks the same way, instead of each one inventing its own convention.
//
// KeyFindings now supports an EPI-deck-style "boxed" variant: a tinted,
// left-accented box with checkmark bullets, modelled on the ✓ bullet boxes
// used throughout the EPI Review deck (e.g. "Summary Analysis" / "The
// Progress" panels). Existing callers that don't pass `variant`/`color` keep
// rendering exactly as before — this is additive, not a replacement.
const KeyFindings = ({ items, variant='plain', color='#1890ff' }) => {
    const filtered = items && items.filter(Boolean);
    if (!filtered || !filtered.length) return null;
    if (variant === 'boxed') {
        return (
            <div style={{ background:`${color}14`, border:`1px solid ${color}55`, borderLeft:`4px solid ${color}`, borderRadius:7, padding:'12px 16px', marginBottom:14 }}>
                {filtered.map((it,i) => (
                    <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', fontSize:16, lineHeight:1.7, color:'#333', marginBottom: i===filtered.length-1?0:6 }}>
                        <span style={{ color, fontWeight:900, flexShrink:0 }}>✓</span>
                        <span>{it}</span>
                    </div>
                ))}
            </div>
        );
    }
    return (
        <ul style={{ margin:'0 0 14px', paddingLeft:18, fontSize:16, lineHeight:1.85, color:'#333' }}>
            {filtered.map((it, i) => <li key={i} style={{ marginBottom:4 }}>{it}</li>)}
        </ul>
    );
};
const ChartNote = ({ children }) => !children ? null : (
    <div style={{ fontSize:13.5, color:'#888', marginTop:6, lineHeight:1.5, fontStyle:'italic' }}>Note: {children}</div>
);
const SourceLine = ({ children }) => !children ? null : (
    <div style={{ fontSize:13, color:'#aaa', marginTop:2, lineHeight:1.5 }}>Source: {children}</div>
);
// Section header used throughout both report bodies — a colored bar with a
// bold title, matching the UN report's Goal-page banner treatment more
// closely than a plain <div> heading. Bumped to 24px/centered, modelled on
// the EPI deck's slide-title treatment (bigger, more "banner", than the
// original 20px left-aligned version).
const SectionBanner = ({ color='#1f3864', children }) => (
    <div style={{ fontSize:30, fontWeight:900, color, textAlign:'center', borderBottom:`3px solid ${color}`, paddingBottom:8, marginBottom:14, letterSpacing:0.3 }}>{children}</div>
);

// HeroStat — one or two big, color-coded numbers with a small label
// underneath, modelled on the EPI deck's headline-metric tiles (e.g. "27.0 /
// 24.8 / 24.7" under "HIV PREVALENCE (%)"). Meant for the single most
// important figure in a section (national/regional average, etc.), not for
// dense multi-metric strips — use SummaryCardStrip for those.
const HeroStat = ({ stats }) => (
    <div style={{ display:'flex', gap:14, marginBottom:16, flexWrap:'wrap', justifyContent:'center' }}>
        {stats.map(s => (
            <div key={s.label} style={{ background:`${s.color||'#1890ff'}14`, border:`1px solid ${s.color||'#1890ff'}40`, borderRadius:10, padding:'14px 28px', textAlign:'center', minWidth:130 }}>
                <div style={{ fontSize:50, fontWeight:900, color:safeColor(s.color||'#1f3864'), lineHeight:1 }}>{s.value}</div>
                <div style={{ fontSize:13.5, color:'#666', textTransform:'uppercase', letterSpacing:0.6, fontWeight:700, marginTop:6 }}>{s.label}</div>
                {s.sub && <div style={{ fontSize:13, color:'#999', marginTop:2 }}>{s.sub}</div>}
            </div>
        ))}
    </div>
);


// ── ME insight helpers ────────────────────────────────────────────────────────
function getMEInsights(meResponses, clauseCode) {
    if (!meResponses || !meResponses[clauseCode]) return null;
    const mes = meResponses[clauseCode];

    const criticalGaps = mes.filter(m =>
        (m.fulfillment==='Not Met'||m.fulfillment==='Partially Met') &&
        (m.criticality_of_observation||'').toLowerCase()==='high' &&
        (m.general_observation||m.comment)
    ).slice(0,3);

    const goodPractices = mes.filter(m =>
        m.area_of_good_practice && m.area_of_good_practice.trim().length > 5
    ).slice(0,3);

    const obsMap = {};
    mes.forEach(m => {
        const obs = (m.general_observation||m.comment||'').trim();
        if (obs.length > 8) obsMap[obs] = (obsMap[obs]||0) + 1;
    });
    const commonObs = Object.entries(obsMap).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([obs])=>obs);

    const notMetMap = {};
    mes.filter(m=>m.fulfillment==='Not Met'&&m.me_text).forEach(m=>{
        const txt = (m.me_text||'').trim();  // full text, no truncation
        notMetMap[txt] = (notMetMap[txt]||0)+1;
    });
    const topNotMet = Object.entries(notMetMap).sort((a,b)=>b[1]-a[1]).slice(0,2).map(([txt,cnt])=>({txt,cnt}));

    return { criticalGaps, goodPractices, commonObs, topNotMet };
}

function buildClauseNarrative(meResponses, clauseCode, clauseName, facScores) {
    const ins = getMEInsights(meResponses, clauseCode);
    const n   = facScores.length;
    if (!n) return null;

    const above70    = facScores.filter(s=>s>=70).length;
    const below70    = n - above70;
    const above70Pct = Math.round((above70/n)*100);
    const below70Pct = 100 - above70Pct;
    const avgSc      = avg(facScores);
    const r          = getRating(avgSc);

    // Summary sentence — factual, no fabrication
    let parts = [];
    parts.push(`${above70Pct}% of facilities (${above70} of ${n}) scored at or above 70% on ${clauseName}, with a regional average of ${avgSc.toFixed(1)}% (${r.label}). ${below70} facilit${below70!==1?'ies':'y'} require improvement in this area.`);

    if (ins) {
        // Good practices — show verbatim
        const gp = (ins.goodPractices||[]).map(g=>(g.area_of_good_practice||'').trim()).filter(t=>t.length>5);
        if (gp.length>0) {
            parts.push(`Good practices noted: ${gp.join('; ')}.`);
        }

        // Key findings — show verbatim auditor observations
        const obs = (ins.commonObs||[]).filter(o=>o.length>8);
        if (obs.length>0) {
            parts.push(`Key findings: ${obs.slice(0,3).join('; ')}.`);
        }

        // Requirements not met — show full me_text verbatim
        const notMet = (ins.topNotMet||[]).filter(g=>g.txt&&g.txt.length>8);
        if (notMet.length>0) {
            parts.push(`Standards not met: ${notMet.map(g=>g.txt.trim()).join('; ')}.`);
        }

        // Critical gaps
        const crit = (ins.criticalGaps||[]).map(g=>(g.general_observation||g.comment||'').trim()).filter(t=>t.length>8);
        if (crit.length>0) {
            parts.push(`⚠ High-priority: ${crit.join('; ')}.`);
        }
    }
    return parts.join(' ');
}

// ── Shared components ─────────────────────────────────────────────────────────
const BarChart = ({ items, showRegion }) => {
    const sorted = [...items].sort((a,b)=>getFacScore(b)-getFacScore(a));
    // Split into two columns
    const half   = Math.ceil(sorted.length / 2);
    const col1   = sorted.slice(0, half);
    const col2   = sorted.slice(half);

    const FacilityRow = ({ f }) => {
        const score = getFacScore(f);
        const r     = getRating(score);
        return (
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5, padding:'4px 6px', background:'#f8faff', borderRadius:5, border:'1px solid #e8f0fe' }}>
                {/* Rank badge + name */}
                <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:'#1f3864', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {f.business_name||f.qi?.facility_name||f.region||'Unknown'}
                    </div>
                    {showRegion && f.located_region && (
                        <div style={{ fontSize:12, color:'#888' }}>{f.located_region}</div>
                    )}
                    {/* Progress bar */}
                    <div style={{ background:'#e8f0fe', borderRadius:3, height:6, marginTop:3, position:'relative' }}>
                        <div style={{ width:`${Math.min(score,100)}%`, height:'100%', background:r.color, borderRadius:3 }}/>
                    </div>
                </div>
                {/* Score + stars */}
                <div style={{ textAlign:'center', flexShrink:0 }}>
                    <div style={{ fontSize:16, fontWeight:900, color:safeColor(r.color) }}>{score.toFixed(1)}%</div>
                    <div style={{ fontSize:12 }}>{'★'.repeat(r.stars)||'☆'}</div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:8 }}>
            <div>{col1.map(f=><FacilityRow key={f.id||f.business_name} f={f}/>)}</div>
            <div>{col2.map(f=><FacilityRow key={f.id||f.business_name} f={f}/>)}</div>
        </div>
    );
};

const Heatmap = ({ facilities, clauses = CLAUSES }) => {
    const sorted = [...facilities].sort((a,b)=>getFacScore(b)-getFacScore(a));
    return (
        <div style={{ overflowX:'auto', marginBottom:8, WebkitPrintColorAdjust:'exact', printColorAdjust:'exact' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11, tableLayout:'fixed' }}>
                <thead>
                    <tr style={{ background:'#1f3864' }}>
                        <th style={{ padding:'5px 8px', color:'#fff', textAlign:'left', minWidth:150, position:'sticky', left:0, background:'#1f3864' }}>Facility</th>
                        <th style={{ padding:'5px 8px', color:'#fff', textAlign:'center', minWidth:55 }}>Overall</th>
                        {clauses.map(cl=>(
                            <th key={cl.code} style={{ padding:'2px 2px', color:'#fff', textAlign:'center', width:44, minWidth:40, writingMode:'vertical-rl', transform:'rotate(180deg)', height:70, fontSize:11 }}>{cl.code}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((f,fi)=>{
                        const overall = getFacScore(f);
                        const r = getRating(overall);
                        return (
                            <tr key={f.id} style={{ background:fi%2?'#fafafa':'#fff' }}>
                                <td style={{ padding:'2px 6px', fontWeight:600, fontSize:11, position:'sticky', left:0, background:fi%2?'#fafafa':'#fff', borderRight:'2px solid #e2e8f0', maxWidth:140, width:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                    {f.business_name}
                                </td>
                                <td style={{ padding:'3px 5px', textAlign:'center', fontWeight:800, color:safeColor(r.color), fontSize:13 }}>{overall.toFixed(1)}%</td>
                                {clauses.map(cl=>{
                                    const score = parseFloat(f.clause_scores?.[cl.code]||0);
                                    const bg    = score>0 ? getRating(score).color : '#f0f0f0';
                                    const text  = score>0 ? (getRating(score).color==='#FFFF00'?'#000':score>=85?'#fff':score>=70?'#000':'#fff') : '#bbb';
                                    return <td key={cl.code} style={{ padding:'2px 1px', textAlign:'center', background:bg, color:text, fontWeight:700, fontSize:10, width:44 }}>{score>0?score.toFixed(0):'—'}</td>;
                                })}
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr style={{ background:'#1f3864', color:'#fff', fontWeight:700 }}>
                        <td style={{ padding:'4px 8px', position:'sticky', left:0, background:'#1f3864', fontSize:12 }}>Regional Average</td>
                        <td style={{ padding:'4px 5px', textAlign:'center', fontSize:13 }}>{avg(sorted.map(f=>getFacScore(f))).toFixed(1)}%</td>
                        {clauses.map(cl=>{
                            const scores = sorted.map(f=>parseFloat(f.clause_scores?.[cl.code]||0)).filter(s=>s>0);
                            const cavg   = scores.length?avg(scores):0;
                            const r      = cavg>0?getRating(cavg):null;
                            return <td key={cl.code} style={{ padding:'4px 2px', textAlign:'center', fontSize:11, color:r?r.color:'#888', background:'#0f172a', fontWeight:800 }}>{cavg>0?cavg.toFixed(0)+'%':'—'}</td>;
                        })}
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

// Reusable legend row for the RATING bands — rounded pill swatches, modelled
// on the EPI deck's small colored-square + label legend rows. Used wherever
// a heatmap/table needs to explain its color coding.
const RatingLegend = () => (
    <div style={{ display:'flex', gap:10, marginBottom:10, flexWrap:'wrap' }}>
        {RATING.map(r=>(
            <div key={r.label} style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, background:'#fafafa', border:'1px solid #eee', borderRadius:20, padding:'3px 9px 3px 4px' }}>
                <div style={{ width:11, height:11, background:r.rowBg, borderRadius:'50%', border:'1px solid rgba(0,0,0,0.1)' }}/>
                <span style={{ color:safeColor(r.color), fontWeight:700 }}>{r.label}</span>
            </div>
        ))}
    </div>
);


// ── Regional Performance Map ──────────────────────────────────────────────────
// Exact SVG path geometry reused from the Executive Dashboard's EswatiniMap
// component, so the report's map matches what's shown elsewhere in the app.
// Differences from the dashboard version: no click-to-filter (this is a static
// print artifact, not an interactive control — filtering happens in the
// Customize drawer instead), and it's fed directly from `regionStats`, which
// is already computed from the live, currently-selected facility set — so a
// facility/clause selection that drops a region also removes it from the map.
const RegionMap = ({ regionStats }) => {
    const byRegion = Object.fromEntries((regionStats||[]).map(r => [r.region, r]));
    const getScore = (r) => byRegion[r] ? byRegion[r].avg.toFixed(1) : '0.0';
    const getCount = (r) => byRegion[r] ? byRegion[r].facs.length : 0;
    const getColor = (r) => byRegion[r] ? (REGION_COLORS[r]||'#94a3b8') : '#cbd5e1';
    return (
        <div style={{ display:'flex', gap:16, alignItems:'flex-start', flexWrap:'wrap' }}>
            <svg viewBox="15 15 570 660" xmlns="http://www.w3.org/2000/svg" style={{ flex:'1 1 280px', minWidth:240, maxWidth:460 }}>
                <path d="M 228.7,267.7 L 227.9,271.1 L 225.7,272.6 L 225.3,273.9 L 224.7,280.4 L 225.3,282.5 L 222.1,284.6 L 227.1,288.3 L 218.9,298.3 L 220.6,305.0 L 218.8,307.8 L 219.0,310.0 L 216.7,313.1 L 217.1,320.9 L 206.2,322.5 L 206.2,323.8 L 209.7,326.1 L 209.1,327.2 L 206.9,326.9 L 208.6,328.8 L 206.9,329.0 L 206.7,330.5 L 204.8,327.5 L 201.9,327.3 L 200.4,331.1 L 198.4,331.9 L 199.3,333.3 L 198.8,334.5 L 197.3,334.1 L 196.6,335.7 L 194.6,335.5 L 194.3,337.8 L 193.6,336.9 L 192.4,337.1 L 193.0,339.2 L 191.5,338.5 L 189.4,339.3 L 190.0,340.9 L 188.9,347.8 L 182.5,336.8 L 183.5,335.8 L 177.4,333.4 L 176.7,319.8 L 175.5,322.4 L 173.2,324.0 L 171.1,323.7 L 169.1,324.5 L 167.9,321.9 L 169.0,321.1 L 168.8,320.3 L 162.9,321.2 L 162.6,317.9 L 153.9,321.3 L 150.2,318.8 L 142.8,318.9 L 131.7,300.5 L 106.3,288.6 L 93.5,280.6 L 83.4,277.1 L 79.9,274.4 L 77.2,273.8 L 73.8,268.4 L 98.3,247.6 L 139.7,174.1 L 148.0,156.6 L 149.2,156.7 L 148.1,156.5 L 158.7,135.8 L 164.3,111.3 L 219.5,66.3 L 239.9,40.0 L 279.8,30.0 L 417.7,116.1 L 417.5,117.0 L 413.0,120.4 L 413.7,122.7 L 411.3,124.0 L 409.5,123.8 L 408.4,122.1 L 405.1,123.2 L 402.5,122.8 L 401.7,121.9 L 403.3,120.3 L 402.3,119.4 L 393.3,121.7 L 390.8,123.4 L 386.8,122.6 L 382.7,119.7 L 381.3,119.5 L 379.5,121.2 L 379.0,150.9 L 384.2,192.7 L 384.7,193.8 L 386.5,194.1 L 388.1,197.4 L 386.7,204.4 L 383.6,203.1 L 384.1,205.6 L 383.4,206.5 L 378.6,208.4 L 370.8,208.7 L 369.2,211.2 L 367.7,210.3 L 364.9,211.5 L 363.7,209.0 L 363.6,209.8 L 361.2,209.3 L 358.5,211.7 L 353.8,209.6 L 351.2,210.2 L 350.2,209.0 L 349.0,209.4 L 348.4,211.6 L 347.1,210.4 L 344.9,210.3 L 343.7,208.4 L 341.0,208.1 L 340.6,208.6 L 342.2,210.9 L 339.9,211.0 L 335.2,214.0 L 330.8,214.9 L 332.6,218.5 L 331.9,221.8 L 333.3,224.4 L 332.8,225.1 L 329.6,225.3 L 328.9,230.2 L 323.5,230.0 L 322.0,228.1 L 320.2,229.4 L 317.0,229.3 L 319.1,231.4 L 318.4,232.2 L 323.8,234.4 L 324.7,236.1 L 319.4,236.0 L 317.1,239.3 L 319.8,241.7 L 314.8,245.7 L 311.4,244.5 L 307.0,245.0 L 306.5,244.3 L 308.4,242.5 L 307.9,242.0 L 301.5,242.6 L 297.6,245.8 L 298.1,247.8 L 296.0,249.9 L 291.3,249.7 L 289.9,251.1 L 288.6,250.4 L 287.0,255.2 L 283.1,256.7 L 279.9,259.8 L 277.8,260.1 L 276.6,261.5 L 274.3,259.2 L 275.4,256.9 L 273.9,255.4 L 271.9,256.5 L 269.5,256.1 L 269.1,257.8 L 266.6,259.3 L 262.7,255.6 L 263.1,253.4 L 259.9,253.3 L 259.3,252.4 L 255.8,255.3 L 252.3,254.3 L 250.9,254.8 L 248.8,253.7 L 248.2,255.6 L 248.8,256.7 L 248.1,257.0 L 241.9,253.7 L 239.6,254.0 L 235.8,252.6 L 235.7,255.6 L 237.2,255.7 L 237.5,256.7 L 236.8,259.1 L 234.2,259.0 L 233.2,258.5 L 233.5,257.5 L 230.2,257.4 L 227.8,260.0 L 229.1,263.8 L 228.7,267.7 Z" style={{ fill:getColor('Hhohho'), stroke:'#fff', strokeWidth:2.5 }}><title>Hhohho</title></path>
                <path d="M 371.0,320.0 L 371.0,323.7 L 369.6,325.1 L 364.0,327.2 L 360.2,326.6 L 356.8,328.5 L 357.3,334.3 L 354.9,336.0 L 356.9,337.9 L 355.3,342.0 L 357.9,347.7 L 357.5,348.9 L 360.9,354.0 L 363.7,354.5 L 362.4,357.1 L 366.0,357.7 L 361.7,361.6 L 358.7,367.5 L 359.8,374.2 L 358.2,377.5 L 360.2,381.5 L 357.4,383.1 L 353.7,383.2 L 351.4,381.5 L 350.5,375.1 L 346.7,375.2 L 338.4,377.6 L 327.2,384.6 L 324.9,386.8 L 326.4,390.6 L 328.7,392.5 L 330.4,399.4 L 328.4,403.6 L 328.8,407.3 L 324.4,412.3 L 318.9,410.5 L 310.8,414.2 L 304.1,410.6 L 300.5,407.5 L 297.1,407.7 L 292.3,403.7 L 290.0,404.6 L 289.6,405.5 L 292.0,411.9 L 289.2,414.9 L 288.8,421.9 L 278.0,426.5 L 276.8,428.5 L 277.3,431.7 L 270.6,434.5 L 270.4,436.1 L 272.1,438.0 L 269.6,438.6 L 266.8,432.3 L 263.2,433.8 L 252.6,441.5 L 246.9,442.8 L 243.6,446.7 L 236.0,441.4 L 233.6,447.4 L 228.3,449.3 L 229.5,452.4 L 228.9,453.1 L 230.5,454.0 L 232.3,458.3 L 224.8,459.8 L 224.1,463.9 L 218.5,464.3 L 216.6,462.3 L 213.5,462.4 L 213.0,460.9 L 211.8,461.7 L 211.2,460.8 L 209.8,462.8 L 208.2,461.3 L 206.4,462.1 L 206.8,463.1 L 205.3,464.7 L 205.8,466.9 L 205.1,467.9 L 200.7,465.7 L 197.9,466.7 L 195.8,467.8 L 196.3,470.0 L 194.3,470.8 L 191.3,475.4 L 187.5,472.3 L 183.6,472.1 L 161.4,484.5 L 161.2,485.6 L 160.3,485.0 L 151.8,488.3 L 144.1,494.1 L 144.4,496.6 L 143.3,497.8 L 143.4,499.5 L 142.2,499.8 L 142.1,499.1 L 140.5,501.5 L 138.1,499.6 L 141.9,486.7 L 133.4,479.9 L 131.0,474.7 L 134.5,473.7 L 134.3,470.7 L 136.0,467.6 L 133.8,465.5 L 134.8,458.2 L 133.9,453.9 L 133.1,453.4 L 92.5,460.4 L 89.4,456.3 L 90.3,452.9 L 87.4,451.6 L 84.5,453.1 L 85.6,454.4 L 85.2,456.5 L 79.6,455.2 L 75.8,457.3 L 72.4,455.6 L 70.3,456.0 L 68.6,462.6 L 69.3,464.3 L 62.2,467.0 L 58.5,470.7 L 54.9,470.2 L 52.6,470.9 L 51.6,472.8 L 48.0,473.8 L 46.1,476.8 L 42.7,479.6 L 39.0,480.3 L 34.7,472.9 L 30.0,429.0 L 30.3,370.6 L 35.7,329.7 L 46.1,307.3 L 56.7,296.4 L 70.4,271.3 L 73.8,268.4 L 77.2,273.8 L 79.9,274.4 L 83.4,277.1 L 93.5,280.6 L 106.3,288.6 L 131.7,300.5 L 142.8,318.9 L 150.2,318.8 L 153.9,321.3 L 162.6,317.9 L 162.9,321.2 L 168.8,320.3 L 169.0,321.1 L 167.9,321.9 L 169.1,324.5 L 171.1,323.7 L 173.2,324.0 L 175.5,322.4 L 176.7,319.8 L 177.4,333.4 L 183.0,335.5 L 188.9,347.8 L 190.0,340.8 L 189.4,339.3 L 191.6,338.5 L 193.0,339.2 L 192.3,337.1 L 193.6,336.9 L 194.3,337.8 L 194.6,335.5 L 196.6,335.7 L 197.3,334.1 L 198.8,334.5 L 199.3,333.3 L 198.4,331.9 L 200.4,331.1 L 201.9,327.3 L 204.8,327.5 L 206.7,330.5 L 206.9,329.0 L 208.6,328.8 L 206.9,326.9 L 209.1,327.2 L 209.7,326.1 L 206.2,323.8 L 206.2,322.5 L 217.1,320.9 L 216.7,313.1 L 219.0,310.0 L 218.8,307.8 L 220.6,305.0 L 218.9,298.3 L 227.1,288.3 L 222.1,284.6 L 225.4,282.5 L 224.7,280.4 L 225.6,272.6 L 227.7,271.4 L 228.6,269.4 L 229.1,263.8 L 227.8,260.0 L 230.2,257.4 L 233.5,257.5 L 233.2,258.5 L 234.2,259.0 L 236.8,259.1 L 237.5,256.7 L 237.1,255.7 L 235.8,255.6 L 235.4,252.8 L 239.6,254.1 L 241.9,253.7 L 248.1,257.0 L 248.8,256.7 L 248.2,255.6 L 248.8,253.7 L 250.9,254.8 L 252.3,254.3 L 255.8,255.3 L 259.3,252.4 L 259.9,253.3 L 263.1,253.4 L 262.7,255.6 L 266.6,259.3 L 269.1,257.8 L 269.5,256.1 L 271.9,256.5 L 273.9,255.4 L 275.4,256.9 L 274.3,259.2 L 276.6,261.5 L 276.9,260.5 L 281.8,258.5 L 283.1,256.7 L 287.0,255.2 L 288.6,250.4 L 290.1,251.1 L 291.3,249.7 L 293.7,250.2 L 297.5,248.9 L 298.2,247.6 L 297.4,245.9 L 301.5,242.7 L 307.9,242.0 L 308.4,242.7 L 306.5,244.4 L 307.0,245.0 L 311.4,244.5 L 313.9,245.8 L 318.9,242.8 L 319.9,241.5 L 317.2,239.3 L 317.9,237.8 L 319.1,237.5 L 319.6,236.0 L 324.8,236.0 L 323.9,234.3 L 318.6,232.3 L 319.1,231.4 L 317.0,229.3 L 320.2,229.4 L 322.0,228.1 L 323.5,230.0 L 328.9,230.2 L 329.6,225.3 L 332.8,225.1 L 333.3,224.4 L 331.9,221.8 L 332.6,218.5 L 330.8,214.9 L 335.2,214.0 L 339.9,211.0 L 342.2,210.9 L 341.0,208.1 L 343.7,208.4 L 344.9,210.3 L 347.1,210.4 L 348.4,211.6 L 349.9,209.0 L 351.2,210.2 L 353.8,209.6 L 358.5,211.7 L 360.8,209.9 L 359.8,212.4 L 361.4,209.3 L 363.9,209.8 L 364.9,211.5 L 367.7,210.3 L 369.2,211.2 L 370.8,208.7 L 378.6,208.4 L 383.6,206.3 L 391.8,237.6 L 388.9,237.3 L 388.1,240.5 L 391.6,239.4 L 392.4,242.0 L 395.4,244.2 L 387.1,249.2 L 388.2,251.7 L 386.9,252.6 L 387.0,271.6 L 379.7,284.0 L 363.8,295.0 L 362.0,299.3 L 366.6,305.7 L 363.5,306.4 L 361.4,309.7 L 361.1,315.0 L 362.2,320.6 L 367.5,320.3 L 369.2,318.0 L 371.0,320.0 Z" style={{ fill:getColor('Manzini'), stroke:'#fff', strokeWidth:2.5 }}><title>Manzini</title></path>
                <path d="M 456.1,566.8 L 458.9,551.1 L 396.3,561.6 L 359.7,547.2 L 353.6,534.6 L 351.8,533.4 L 349.1,534.3 L 345.0,533.1 L 347.9,530.8 L 342.0,530.1 L 342.7,532.1 L 339.9,532.7 L 339.2,532.1 L 340.6,530.8 L 338.4,529.1 L 336.5,531.3 L 336.5,532.5 L 335.1,532.2 L 334.6,530.2 L 333.3,530.2 L 331.2,532.0 L 328.6,531.4 L 329.6,520.7 L 327.7,522.9 L 325.2,523.2 L 325.2,520.3 L 323.7,517.4 L 323.0,518.5 L 321.2,518.0 L 320.8,517.0 L 323.0,516.2 L 323.8,514.9 L 320.9,513.5 L 319.5,505.8 L 314.9,502.4 L 314.7,499.9 L 317.3,495.7 L 319.1,494.6 L 319.7,492.6 L 318.6,488.4 L 321.2,485.8 L 322.4,487.3 L 321.6,488.8 L 322.6,489.1 L 322.5,491.0 L 325.9,489.0 L 327.2,490.3 L 330.1,490.5 L 328.9,493.5 L 331.6,493.7 L 332.6,492.0 L 329.4,486.7 L 330.9,483.3 L 334.0,482.6 L 335.7,480.6 L 341.5,482.6 L 344.5,481.6 L 346.5,482.4 L 347.8,477.6 L 346.7,473.1 L 350.3,473.4 L 353.8,469.1 L 352.1,466.5 L 352.2,462.7 L 348.3,460.1 L 344.6,460.8 L 343.3,462.4 L 342.5,460.8 L 343.6,458.5 L 342.9,457.7 L 344.2,456.8 L 340.5,450.7 L 337.4,449.5 L 336.3,443.4 L 332.0,444.6 L 327.8,442.8 L 325.0,438.9 L 325.2,437.8 L 322.1,434.4 L 321.3,430.2 L 323.8,424.0 L 322.8,423.1 L 321.0,424.6 L 318.5,424.5 L 316.3,423.2 L 318.2,416.8 L 318.5,410.6 L 324.4,412.3 L 329.1,407.1 L 328.2,406.2 L 328.4,403.7 L 330.4,399.4 L 328.8,392.6 L 326.4,390.6 L 325.1,386.7 L 327.3,384.6 L 332.5,382.0 L 338.5,377.6 L 346.5,375.2 L 350.5,375.2 L 351.4,381.6 L 353.9,383.1 L 357.4,383.1 L 360.2,381.5 L 358.2,377.5 L 359.8,374.2 L 358.7,367.5 L 362.5,360.4 L 366.1,357.5 L 366.4,358.1 L 365.6,357.3 L 362.6,357.4 L 363.7,354.5 L 360.9,354.0 L 357.5,348.9 L 357.9,347.7 L 355.3,342.0 L 356.9,337.9 L 355.0,336.0 L 357.3,334.3 L 356.5,329.2 L 357.8,327.7 L 360.2,326.6 L 364.6,327.1 L 368.4,324.9 L 369.6,325.1 L 371.0,323.7 L 371.0,320.0 L 369.4,318.0 L 367.4,320.3 L 361.6,320.7 L 362.3,318.6 L 361.1,315.0 L 361.4,309.8 L 363.5,306.4 L 366.6,305.7 L 362.0,299.3 L 364.0,295.0 L 379.7,284.0 L 387.0,271.6 L 386.9,252.6 L 388.2,251.7 L 387.1,249.2 L 395.4,244.2 L 392.4,242.0 L 391.6,239.4 L 388.1,240.5 L 388.9,237.3 L 391.8,237.6 L 383.6,206.3 L 383.6,203.1 L 386.8,204.2 L 388.2,197.6 L 386.5,194.2 L 384.7,193.8 L 384.2,192.7 L 379.0,150.9 L 379.5,121.2 L 381.4,119.5 L 386.8,122.7 L 390.8,123.4 L 393.3,121.7 L 402.3,119.4 L 403.3,120.3 L 401.7,121.9 L 402.5,122.8 L 405.1,123.2 L 408.4,122.1 L 409.5,123.8 L 411.3,124.0 L 413.5,123.0 L 412.9,120.5 L 416.3,118.4 L 417.7,116.1 L 461.4,142.9 L 503.8,124.2 L 514.7,141.8 L 517.9,140.5 L 525.6,143.2 L 548.3,146.5 L 548.1,158.1 L 555.5,207.1 L 543.1,265.2 L 544.3,305.1 L 567.4,353.9 L 567.3,479.0 L 561.6,478.3 L 559.4,474.0 L 557.0,471.9 L 556.0,466.8 L 553.1,464.8 L 542.3,466.9 L 537.9,469.8 L 535.3,470.3 L 531.9,469.8 L 528.1,466.1 L 525.7,465.3 L 518.1,467.1 L 519.8,471.9 L 512.9,511.3 L 513.4,514.8 L 511.1,532.4 L 508.0,545.8 L 504.3,608.5 L 475.6,608.7 L 478.6,600.5 L 458.0,600.6 L 459.9,584.9 L 451.6,584.9 L 454.3,574.1 L 456.5,572.9 L 456.1,566.8 Z" style={{ fill:getColor('Lubombo'), stroke:'#fff', strokeWidth:2.5 }}><title>Lubombo</title></path>
                <path d="M 343.1,455.6 L 344.2,456.8 L 342.9,457.7 L 343.6,458.5 L 342.5,460.8 L 343.3,462.4 L 344.6,460.8 L 348.3,460.1 L 352.2,462.7 L 352.1,466.5 L 353.8,469.1 L 350.3,473.4 L 346.7,473.1 L 347.8,477.6 L 347.1,481.8 L 341.5,482.6 L 335.7,480.6 L 334.0,482.6 L 330.9,483.3 L 329.5,486.7 L 332.6,492.3 L 330.6,494.0 L 328.7,493.1 L 330.0,490.4 L 327.2,490.3 L 325.9,489.0 L 322.5,490.9 L 322.6,489.1 L 321.6,488.8 L 322.4,487.3 L 321.2,485.8 L 318.6,488.4 L 319.7,492.6 L 319.2,494.5 L 317.3,495.7 L 314.7,500.1 L 314.9,502.4 L 318.6,505.1 L 318.7,504.1 L 319.7,506.1 L 320.9,513.5 L 323.8,514.9 L 323.0,516.2 L 320.9,516.9 L 321.2,518.0 L 323.0,518.5 L 323.7,517.4 L 325.2,520.3 L 325.3,523.4 L 327.7,522.9 L 329.6,520.7 L 328.6,531.4 L 331.2,532.0 L 333.3,530.2 L 334.6,530.2 L 335.1,532.2 L 336.5,532.5 L 336.5,531.3 L 338.4,529.1 L 340.6,530.8 L 339.2,532.1 L 339.9,532.7 L 342.7,532.1 L 342.0,530.1 L 347.9,530.8 L 345.0,533.1 L 349.1,534.3 L 351.8,533.4 L 353.6,534.6 L 359.7,547.2 L 396.3,561.6 L 458.9,551.1 L 455.8,566.8 L 456.5,572.9 L 454.3,574.1 L 451.6,584.9 L 459.9,584.9 L 458.0,600.6 L 478.3,600.5 L 475.6,608.7 L 504.3,608.5 L 503.6,614.6 L 504.7,620.6 L 504.1,632.7 L 506.6,639.8 L 507.0,643.9 L 505.5,646.7 L 505.3,653.7 L 504.1,657.2 L 504.5,670.0 L 311.0,669.1 L 173.9,623.7 L 138.2,581.4 L 122.3,570.0 L 107.1,556.8 L 96.9,539.9 L 103.0,528.7 L 104.4,523.4 L 104.4,511.5 L 103.0,506.1 L 93.3,499.0 L 91.0,498.5 L 89.9,496.5 L 88.5,496.2 L 80.8,489.3 L 76.5,487.0 L 76.4,484.9 L 74.8,483.8 L 71.0,465.0 L 70.4,463.9 L 69.3,464.3 L 68.7,462.8 L 70.3,455.9 L 72.5,455.6 L 75.8,457.3 L 79.5,455.2 L 85.2,456.5 L 85.6,454.4 L 84.5,453.2 L 87.0,451.6 L 90.2,452.9 L 89.4,456.3 L 92.5,460.4 L 133.7,453.6 L 134.9,461.3 L 133.9,465.6 L 136.0,467.6 L 134.3,470.7 L 134.5,473.7 L 131.0,474.7 L 133.4,479.9 L 141.9,486.7 L 138.1,499.6 L 140.5,501.5 L 142.1,499.1 L 142.2,499.8 L 143.4,499.5 L 143.3,497.8 L 144.4,496.6 L 144.1,494.1 L 151.8,488.3 L 160.3,485.0 L 161.2,485.6 L 161.4,484.5 L 183.6,472.1 L 187.5,472.3 L 191.3,475.4 L 194.3,470.8 L 196.3,470.0 L 195.8,467.8 L 197.9,466.7 L 200.7,465.7 L 205.1,467.9 L 205.8,466.9 L 205.3,464.7 L 206.8,463.1 L 206.4,462.1 L 208.2,461.3 L 209.8,462.8 L 211.2,460.8 L 211.8,461.7 L 213.0,460.9 L 213.5,462.4 L 216.6,462.3 L 218.5,464.3 L 224.1,463.9 L 224.8,459.8 L 232.3,458.2 L 230.5,454.1 L 228.9,453.1 L 229.3,451.8 L 228.1,450.0 L 228.5,449.0 L 233.6,447.3 L 235.9,441.4 L 243.6,446.7 L 247.6,442.3 L 252.6,441.7 L 253.3,440.5 L 254.5,440.5 L 262.8,434.1 L 266.8,432.2 L 269.6,438.6 L 271.9,438.2 L 272.2,437.4 L 270.4,436.1 L 270.6,434.5 L 277.3,431.7 L 276.8,428.5 L 278.0,426.5 L 288.8,421.9 L 289.2,414.9 L 292.0,411.9 L 289.6,405.2 L 291.9,404.1 L 296.6,407.4 L 301.2,407.7 L 304.6,410.9 L 310.4,414.1 L 313.0,413.8 L 318.5,410.6 L 318.2,416.8 L 316.3,423.3 L 318.6,424.5 L 321.0,424.6 L 322.8,423.1 L 323.8,424.0 L 321.3,430.2 L 322.1,434.4 L 325.2,437.8 L 325.0,438.9 L 327.8,442.8 L 332.0,444.6 L 336.3,443.4 L 337.4,449.5 L 340.5,450.7 L 343.1,455.6 Z" style={{ fill:getColor('Shiselweni'), stroke:'#fff', strokeWidth:2.5 }}><title>Shiselweni</title></path>
                <g><text x="243" y="186" textAnchor="middle" style={{ fontSize:16, fontWeight:800, fill:'#fff' }}>Hhohho</text><text x="243" y="202" textAnchor="middle" style={{ fontSize:17, fontWeight:900, fill:'#fff' }}>{getScore('Hhohho')}%</text><text x="243" y="216" textAnchor="middle" style={{ fontSize:13, fill:'rgba(255,255,255,0.85)' }}>{getCount('Hhohho')} fac.</text></g>
                <g><text x="200" y="366" textAnchor="middle" style={{ fontSize:16, fontWeight:800, fill:'#fff' }}>Manzini</text><text x="200" y="382" textAnchor="middle" style={{ fontSize:17, fontWeight:900, fill:'#fff' }}>{getScore('Manzini')}%</text><text x="200" y="396" textAnchor="middle" style={{ fontSize:13, fill:'rgba(255,255,255,0.85)' }}>{getCount('Manzini')} fac.</text></g>
                <g><text x="450" y="351" textAnchor="middle" style={{ fontSize:16, fontWeight:800, fill:'#fff' }}>Lubombo</text><text x="450" y="367" textAnchor="middle" style={{ fontSize:17, fontWeight:900, fill:'#fff' }}>{getScore('Lubombo')}%</text><text x="450" y="381" textAnchor="middle" style={{ fontSize:13, fill:'rgba(255,255,255,0.85)' }}>{getCount('Lubombo')} fac.</text></g>
                <g><text x="285" y="561" textAnchor="middle" style={{ fontSize:16, fontWeight:800, fill:'#fff' }}>Shiselweni</text><text x="285" y="577" textAnchor="middle" style={{ fontSize:17, fontWeight:900, fill:'#fff' }}>{getScore('Shiselweni')}%</text><text x="285" y="591" textAnchor="middle" style={{ fontSize:13, fill:'rgba(255,255,255,0.85)' }}>{getCount('Shiselweni')} fac.</text></g>
            </svg>
            <div style={{ flex:'0 0 150px', display:'flex', flexDirection:'column', gap:8, paddingTop:16 }}>
                <div style={{ fontSize:15, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>Regions</div>
                {REGION_LIST.map(r => (
                    <div key={r} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 8px' }}>
                        <div style={{ width:14, height:14, borderRadius:3, flexShrink:0, background:REGION_COLORS[r] }}/>
                        <div>
                            <div style={{ fontSize:16, fontWeight:700, color:'#1e293b' }}>{r}</div>
                            <div style={{ fontSize:15, color:'#64748b' }}>{getScore(r)}% · {getCount(r)} fac.</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};


// ── Clause Radar + Bar (matches the Executive Dashboard's "Standard — Radar" /
// "Standard — Clause Scores" pairing) ─────────────────────────────────────────
// clauseData here is the report's own already-filtered clauseData array (only
// enabled clauses, computed from the currently-selected facilities), so this
// visual automatically reflects the same selection as everything else.
const ClauseRadarBar = ({ clauseData }) => {
    if (!clauseData || clauseData.length === 0) return null;
    const showRadar = clauseData.length >= 3; // radar needs 3+ axes to be meaningful
    const radarOption = {
        tooltip: { trigger:'item' },
        radar: {
            indicator: clauseData.map(c=>({ name:c.code, max:100 })),
            radius: '65%', center: ['50%','52%'],
            axisName: { fontSize:15, fontWeight:700 }, splitNumber:5,
            splitArea: { areaStyle: { color:['#f8f9fa','#fff'] } },
        },
        series: [{ type:'radar', data:[{
            value: clauseData.map(c=>parseFloat(c.cavg.toFixed(1))),
            areaStyle: { color:'rgba(24,144,255,0.18)' },
            lineStyle: { color:'#1890ff', width:2 },
            itemStyle: { color:'#1890ff' },
        }] }],
    };
    const sorted = [...clauseData].sort((a,b)=>a.cavg-b.cavg);
    const barOption = {
        tooltip: { trigger:'axis' },
        grid: { top:10, right:60, bottom:20, left:60 },
        xAxis: { type:'value', max:100, axisLabel:{ formatter:v=>`${v}%`, fontSize:12 } },
        yAxis: { type:'category', data: sorted.map(c=>c.code), axisLabel:{ fontSize:15, fontWeight:700 } },
        series: [{ type:'bar', barMaxWidth:18,
            data: sorted.map(c=>({ value:parseFloat(c.cavg.toFixed(1)), itemStyle:{ color:getRating(c.cavg).color, borderRadius:[0,5,5,0] } })),
            label: { show:true, position:'right', formatter:p=>`${p.value}%`, fontSize:13 },
        }],
    };
    return (
        <Row gutter={16} style={{ marginBottom:16 }}>
            {showRadar && (
                <Col span={10}>
                    <div style={{ fontSize:15, fontWeight:700, color:'#1f3864', marginBottom:6 }}>CLAUSE RADAR</div>
                    <ReactECharts option={radarOption} style={{ height:260 }}/>
                </Col>
            )}
            <Col span={showRadar ? 14 : 24}>
                <div style={{ fontSize:15, fontWeight:700, color:'#1f3864', marginBottom:6 }}>CLAUSE SCORES</div>
                <ReactECharts option={barOption} style={{ height: Math.max(200, sorted.length*30+40) }}/>
            </Col>
        </Row>
    );
};


// ── Period Comparison Chart ───────────────────────────────────────────────────
// Grouped bar: current period's clauseData (already filtered to the current
// facility/clause selection, same as every other section) against a second,
// user-picked period. The comparison period's data is fetched server-side
// with the SAME facility_id/clause_code filters (see the debounced effect in
// the main component below), so "filtered too" holds for this chart as well
// — it isn't comparing against a differently-scoped dataset.
const PeriodComparisonChart = ({ currentClauseData, currentLabel, comparisonScores, comparisonLabel }) => {
    if (!currentClauseData || !currentClauseData.length || !comparisonScores) return null;
    const codes = currentClauseData.map(c=>c.code);
    const option = {
        tooltip: { trigger:'axis' },
        legend: { data:[currentLabel, comparisonLabel], bottom:0, textStyle:{ fontSize:15 } },
        grid: { top:20, right:20, bottom:55, left:55 },
        xAxis: { type:'category', data: codes, axisLabel:{ fontSize:13 } },
        yAxis: { type:'value', min:0, max:100, axisLabel:{ formatter:v=>`${v}%`, fontSize:13 } },
        series: [
            {
                name: currentLabel, type:'bar', barMaxWidth:22,
                data: currentClauseData.map(c=>({ value:parseFloat(c.cavg.toFixed(1)), itemStyle:{ color:'#1890ff', borderRadius:[3,3,0,0] } })),
                label: { show:true, position:'top', fontSize:12, formatter:p=>p.value>0?`${p.value}%`:'' },
            },
            {
                name: comparisonLabel, type:'bar', barMaxWidth:22,
                data: codes.map(code => {
                    const v = comparisonScores[code];
                    return v != null
                        ? { value:parseFloat(v.toFixed(1)), itemStyle:{ color:'#95de64', borderRadius:[3,3,0,0] } }
                        : { value:0, itemStyle:{ opacity:0 } };
                }),
                label: { show:true, position:'top', fontSize:12, formatter:p=>p.value>0?`${p.value}%`:'' },
            },
        ],
    };
    return <ReactECharts option={option} style={{ height:320 }}/>;
};


// ── Summary Card Strip ────────────────────────────────────────────────────────
// A row of small KPI-style cards. Used for the national/regional aggregate
// cards on Quality Index and Clause Performance Summary — the same visual
// language QualityIndexTable already uses for its own per-facility-type
// strip, just reusable for a single overarching aggregate instead of one
// strip per group. Backgrounds are now a pastel tint of each card's own
// color (EPI-deck style) instead of a flat blue-white, so a red/orange KPI
// visually reads as such even before the number is parsed.
const SummaryCardStrip = ({ cards }) => (
    <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        {cards.map(k => (
            <div key={k.label} style={{ background:`${k.color||'#1890ff'}17`, border:`1px solid ${k.color||'#1890ff'}40`, borderLeft:`3px solid ${k.color||'#1890ff'}`, borderRadius:8, padding:'10px 14px', textAlign:'center', flex:1, minWidth:110 }}>
                <div style={{ fontSize:12, color:'#888', textTransform:'uppercase', letterSpacing:0.5, marginBottom:3 }}>{k.label}</div>
                <div style={{ fontSize:22, fontWeight:900, color:safeColor(k.color||'#1f3864') }}>{k.val}</div>
                {k.sub && <div style={{ fontSize:12, color:'#aaa', marginTop:2 }}>{k.sub}</div>}
            </div>
        ))}
    </div>
);


// ── Quality Index Table ───────────────────────────────────────────────────────
const QualityIndexTable = ({ facilities, title }) => {
    const withQI = facilities.filter(f => f.qi && parseFloat(f.qi.quality_index||0) > 0)
        .sort((a,b) => parseFloat(b.qi.quality_index||0) - parseFloat(a.qi.quality_index||0));
    const withoutQI = facilities.filter(f => !f.qi || parseFloat(f.qi.quality_index||0) === 0);

    if (!withQI.length && !withoutQI.length) return null;

    const avgQI  = withQI.length ? avg(withQI.map(f=>parseFloat(f.qi.quality_index||0))) : 0;
    const avgQoC = withQI.length ? avg(withQI.map(f=>parseFloat(f.qi.qoc_score||0)))     : 0;
    const avgSrv = withQI.length ? avg(withQI.map(f=>parseFloat(f.qi.survey_composite||0))) : 0;
    const rqi    = getRating(avgQI);

    return (
        <div style={{ marginBottom:10 }}>
            {title && <div style={{ fontWeight:700, fontSize:16, color:'#1f3864', marginBottom:8 }}>{title}</div>}

            {/* Summary strip — tinted per-card, same pastel treatment as SummaryCardStrip */}
            <div style={{ display:'flex', gap:10, marginBottom:12, flexWrap:'wrap' }}>
                {[
                    { label:'Avg Quality Index', val:`${avgQI.toFixed(1)}%`, color:safeColor(rqi.color) },
                    { label:'Avg QoC Score (80%)', val:`${avgQoC.toFixed(1)}%`, color:'#1890ff' },
                    { label:'Avg Survey Score (20%)', val:`${avgSrv.toFixed(1)}%`, color:'#52c41a' },
                    { label:'Facilities with QI', val:withQI.length, color:'#333' },
                    { label:'Pending Calculation', val:withoutQI.length, color:'#fa8c16' },
                ].map(k=>(
                    <div key={k.label} style={{ background:`${k.color}17`, border:`1px solid ${k.color}40`, borderRadius:8, padding:'6px 10px', textAlign:'center', flex:1, minWidth:80 }}>
                        <div style={{ fontSize:12, color:'#888', marginBottom:2 }}>{k.label}</div>
                        <div style={{ fontSize:18, fontWeight:800, color:safeColor(k.color) }}>{k.val}</div>
                    </div>
                ))}
            </div>

            {(() => {
                // Same 2-column treatment as FacilityDetailTable, and for the
                // same reason — long per-facility lists spread across many
                // pages as a single table. Reduced to Facility/Quality
                // Index/Rating in 2-column mode, matching the same tradeoff.
                const TWO_COL_THRESHOLD = 15;
                if (withQI.length > TWO_COL_THRESHOLD) {
                    const mid  = Math.ceil(withQI.length / 2);
                    const colA = withQI.slice(0, mid);
                    const colB = withQI.slice(mid);
                    const renderMiniTable = (rows, startIndex) => (
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
                            <thead>
                                <tr style={{ background:'#1f3864' }}>
                                    <th style={{ padding:'5px 7px', color:'#fff', fontWeight:700, textAlign:'left', border:'1px solid #ddd' }}>Facility</th>
                                    <th style={{ padding:'5px 7px', color:'#fff', fontWeight:700, textAlign:'center', border:'1px solid #ddd', whiteSpace:'nowrap' }}>Quality Index</th>
                                    <th style={{ padding:'5px 7px', color:'#fff', fontWeight:700, textAlign:'center', border:'1px solid #ddd' }}>Rating</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((f,i)=>{
                                    const qi = parseFloat(f.qi.quality_index||0);
                                    const r  = getRating(qi);
                                    return (
                                        <tr key={f.id} style={{ background:(startIndex+i)%2?'#fafafa':'#fff' }}>
                                            <td style={{ padding:'4px 7px', fontWeight:600, border:'1px solid #eee' }}>{f.business_name}</td>
                                            <td style={{ padding:'4px 7px', textAlign:'center', fontWeight:800, color:safeColor(r.color), border:'1px solid #eee' }}>{qi.toFixed(1)}%</td>
                                            <td style={{ padding:'4px 7px', textAlign:'center', border:'1px solid #eee' }}>
                                                <span style={{ background:r.rowBg, color:r.rowText, padding:'1px 5px', borderRadius:4, fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>{r.label}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    );
                    return (
                        <>
                            <div style={{ display:'flex', gap:16 }}>
                                <div style={{ flex:1, minWidth:0 }}>{renderMiniTable(colA, 0)}</div>
                                <div style={{ flex:1, minWidth:0 }}>{renderMiniTable(colB, mid)}</div>
                            </div>
                            {withoutQI.length > 0 && (
                                <div style={{ padding:'5px 8px', color:'#fa8c16', fontSize:13, background:'#fff7e6', border:'1px solid #eee', marginTop:8 }}>
                                    ⚠ {withoutQI.length} facilit{withoutQI.length>1?'ies':'y'} pending quality index calculation: {withoutQI.map(f=>f.business_name).join(', ')}
                                </div>
                            )}
                        </>
                    );
                }
                return (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                    <tr style={{ background:'#1f3864' }}>
                        {['Facility','Type','QoC Score','Survey Score','Quality Index','Rating'].map(h=>(
                            <th key={h} style={{ padding:'6px 8px', color:'#fff', fontWeight:700, textAlign:h==='Facility'?'left':'center', border:'1px solid #ddd' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {withQI.map((f,i)=>{
                        const qi  = parseFloat(f.qi.quality_index||0);
                        const r   = getRating(qi);
                        const qoc = parseFloat(f.qi.qoc_score||0);
                        const srv = parseFloat(f.qi.survey_composite||0);
                        const hasSurvey = parseInt(f.qi.inpatient_count||0)+parseInt(f.qi.outpatient_count||0) > 0;
                        return (
                            <tr key={f.id} style={{ background:i%2?'#fafafa':'#fff' }}>
                                <td style={{ padding:'5px 8px', fontWeight:600, border:'1px solid #eee' }}>{f.business_name}</td>
                                <td style={{ padding:'5px 8px', textAlign:'center', fontSize:12, color:'#666', border:'1px solid #eee' }}>{f.site_type||f.qi?.site_type||'—'}</td>
                                <td style={{ padding:'5px 8px', textAlign:'center', border:'1px solid #eee' }}>
                                    <div style={{ fontWeight:700, color:'#1890ff' }}>{qoc.toFixed(1)}%</div>
                                    <div style={{ fontSize:12, color:'#aaa' }}>×{f.qi.qoc_weight||80}% = {(qoc*(f.qi.qoc_weight||80)/100).toFixed(1)}pts</div>
                                </td>
                                <td style={{ padding:'5px 8px', textAlign:'center', border:'1px solid #eee' }}>
                                    <div style={{ fontWeight:700, color: hasSurvey?'#52c41a':'#fa8c16' }}>{srv.toFixed(1)}%</div>
                                    {!hasSurvey && <div style={{ fontSize:12, color:'#fa8c16' }}>No surveys</div>}
                                    {hasSurvey && <div style={{ fontSize:12, color:'#aaa' }}>×{f.qi.survey_weight||20}% = {(srv*(f.qi.survey_weight||20)/100).toFixed(1)}pts</div>}
                                </td>
                                <td style={{ padding:'5px 8px', textAlign:'center', border:'1px solid #eee' }}>
                                    <div style={{ fontWeight:900, fontSize:18, color:safeColor(r.color) }}>{qi.toFixed(1)}%</div>
                                    <div style={{ background:'#f0f0f0', borderRadius:3, height:6, marginTop:3, overflow:'hidden' }}>
                                        <div style={{ width:`${Math.min(qi,100)}%`, height:'100%', background:r.color, borderRadius:3 }}/>
                                    </div>
                                </td>
                                <td style={{ padding:'5px 8px', textAlign:'center', border:'1px solid #eee' }}>
                                    <span style={{ background:r.rowBg, color:r.rowText, padding:'2px 6px', borderRadius:4, fontSize:12, fontWeight:700 }}>{r.label}</span>
                                </td>
                            </tr>
                        );
                    })}
                    {withoutQI.length > 0 && (
                        <tr>
                            <td colSpan={6} style={{ padding:'5px 8px', color:'#fa8c16', fontSize:13, background:'#fff7e6', border:'1px solid #eee' }}>
                                ⚠ {withoutQI.length} facilit{withoutQI.length>1?'ies':'y'} pending quality index calculation: {withoutQI.map(f=>f.business_name).join(', ')}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
                );
            })()}
        </div>
    );
};


// ── Facility Detail Table ─────────────────────────────────────────────────────
// A summary table across all currently-selected facilities. Deliberately a
// single table rather than one page per facility — for a report scoped to
// dozens or a hundred+ facilities, a scannable table is far more usable than
// a wall of individual detail pages. Every column respects the current
// clause/facility selection: "Best/Weakest Clause" are computed only from
// clauses still enabled, and "Critical Gaps" only counts gaps in enabled
// clauses (since meResponses passed in is already clause-filtered upstream).
const FacilityDetailTable = ({ facilities, meResponses, clauses = CLAUSES, showRegion }) => {
    // Critical (high-severity, Not Met/Partially Met) gap count per facility,
    // keyed by business_name to match how meResponses entries are tagged.
    const critCounts = {};
    if (meResponses) {
        clauses.forEach(cl => {
            (meResponses[cl.code]||[]).forEach(m => {
                const isCrit = (m.criticality_of_observation||'').toLowerCase()==='high' &&
                    (m.fulfillment==='Not Met'||m.fulfillment==='Partially Met');
                if (isCrit) {
                    const name = (m.business_name||'').trim();
                    if (name) critCounts[name] = (critCounts[name]||0) + 1;
                }
            });
        });
    }

    const sorted = [...facilities].sort((a,b)=>getFacScore(b)-getFacScore(a));
    const headers = ['Facility', ...(showRegion?['Region']:[]), 'Type', 'Overall %', 'Rating', 'Quality Index', 'Best Clause', 'Weakest Clause', ...(meResponses?['Critical Gaps']:[])];

    // Long facility lists (national reports especially) can run to 100+ rows
    // as a single table, spreading across many pages. Past a threshold,
    // split into two side-by-side columns instead — but a 2-column layout
    // only has half the page width to work with, so the reduced-detail
    // columns (Facility, Overall %, Rating) are shown rather than cramming
    // all 9 columns into each half. Short lists keep full detail, since
    // there's no space problem to solve for those.
    const TWO_COL_THRESHOLD = 15;
    if (sorted.length > TWO_COL_THRESHOLD) {
        const mid  = Math.ceil(sorted.length / 2);
        const colA = sorted.slice(0, mid);
        const colB = sorted.slice(mid);
        const renderMiniTable = (rows, startIndex) => (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
                <thead>
                    <tr style={{ background:'#1f3864' }}>
                        <th style={{ padding:'5px 7px', color:'#fff', fontWeight:700, textAlign:'left', border:'1px solid #ddd' }}>Facility</th>
                        <th style={{ padding:'5px 7px', color:'#fff', fontWeight:700, textAlign:'center', border:'1px solid #ddd', whiteSpace:'nowrap' }}>Overall %</th>
                        <th style={{ padding:'5px 7px', color:'#fff', fontWeight:700, textAlign:'center', border:'1px solid #ddd' }}>Rating</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((f,i)=>{
                        const overall = getFacScore(f);
                        const r = getRating(overall);
                        return (
                            <tr key={f.id} style={{ background:(startIndex+i)%2?'#fafafa':'#fff' }}>
                                <td style={{ padding:'4px 7px', fontWeight:600, border:'1px solid #eee' }}>{f.business_name}</td>
                                <td style={{ padding:'4px 7px', textAlign:'center', fontWeight:800, color:safeColor(r.color), border:'1px solid #eee' }}>{overall.toFixed(1)}%</td>
                                <td style={{ padding:'4px 7px', textAlign:'center', border:'1px solid #eee' }}>
                                    <span style={{ background:r.rowBg, color:r.rowText, padding:'1px 5px', borderRadius:4, fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>{'★'.repeat(r.stars)||'—'} {r.label}</span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
        return (
            <div style={{ display:'flex', gap:16 }}>
                <div style={{ flex:1, minWidth:0 }}>{renderMiniTable(colA, 0)}</div>
                <div style={{ flex:1, minWidth:0 }}>{renderMiniTable(colB, mid)}</div>
            </div>
        );
    }

    return (
        <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, minWidth:720 }}>
                <thead>
                    <tr style={{ background:'#1f3864' }}>
                        {headers.map(h=>(
                            <th key={h} style={{ padding:'6px 8px', color:'#fff', fontWeight:700, textAlign: h==='Facility'?'left':'center', border:'1px solid #ddd', whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((f,i)=>{
                        const overall = getFacScore(f);
                        const r = getRating(overall);
                        const clauseEntries = clauses
                            .map(cl=>({ code:cl.code, color:cl.color, score:parseFloat(f.clause_scores?.[cl.code]||0) }))
                            .filter(c=>c.score>0);
                        const best  = clauseEntries.length ? clauseEntries.reduce((a,b)=>a.score>b.score?a:b) : null;
                        const worst = clauseEntries.length ? clauseEntries.reduce((a,b)=>a.score<b.score?a:b) : null;
                        const qi = f.qi ? parseFloat(f.qi.quality_index||0) : null;
                        const critCount = critCounts[(f.business_name||'').trim()] || 0;
                        return (
                            <tr key={f.id} style={{ background:i%2?'#fafafa':'#fff' }}>
                                <td style={{ padding:'5px 8px', fontWeight:600, border:'1px solid #eee' }}>{f.business_name}</td>
                                {showRegion && <td style={{ padding:'5px 8px', textAlign:'center', fontSize:12, color:'#666', border:'1px solid #eee' }}>{f.located_region||f.region||'—'}</td>}
                                <td style={{ padding:'5px 8px', textAlign:'center', fontSize:12, color:'#666', border:'1px solid #eee' }}>{f.site_type||'—'}</td>
                                <td style={{ padding:'5px 8px', textAlign:'center', fontWeight:800, color:safeColor(r.color), border:'1px solid #eee' }}>{overall.toFixed(1)}%</td>
                                <td style={{ padding:'5px 8px', textAlign:'center', border:'1px solid #eee' }}>
                                    <span style={{ background:r.rowBg, color:r.rowText, padding:'1px 6px', borderRadius:4, fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>{'★'.repeat(r.stars)||'—'} {r.label}</span>
                                </td>
                                <td style={{ padding:'5px 8px', textAlign:'center', fontWeight:700, color:'#7c3aed', border:'1px solid #eee' }}>{qi!==null && qi>0 ? qi.toFixed(1)+'%' : '—'}</td>
                                <td style={{ padding:'5px 8px', textAlign:'center', border:'1px solid #eee' }}>
                                    {best ? <span><span style={{ fontWeight:700, color:best.color }}>{best.code}</span> {best.score.toFixed(0)}%</span> : '—'}
                                </td>
                                <td style={{ padding:'5px 8px', textAlign:'center', border:'1px solid #eee' }}>
                                    {worst ? <span><span style={{ fontWeight:700, color:worst.color }}>{worst.code}</span> {worst.score.toFixed(0)}%</span> : '—'}
                                </td>
                                {meResponses && (
                                    <td style={{ padding:'5px 8px', textAlign:'center', fontWeight:700, color: critCount>0?'#f5222d':'#389e0d', border:'1px solid #eee' }}>
                                        {critCount || '—'}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};


// ── Qualitative Comments Component ───────────────────────────────────────────
const QualitativeComments = ({ meResponses, facilities, clauses = CLAUSES }) => {
    if (!meResponses || Object.keys(meResponses).length === 0) return null;

    // Build per-facility gap map — include ME requirement text
    const facilityMap = {};
    clauses.forEach(cl => {
        const mes = meResponses[cl.code] || [];
        mes.forEach(me => {
            const fac = (me.business_name || '').trim();
            if (!fac) return;
            if (!facilityMap[fac]) facilityMap[fac] = { clauses:{}, totalGaps:0, totalGood:0, totalCrit:0 };
            if (!facilityMap[fac].clauses[cl.code]) facilityMap[fac].clauses[cl.code] = { gaps:[], good:[] };

            const entry  = facilityMap[fac].clauses[cl.code];
            const isBad  = me.fulfillment === 'Not Met' || me.fulfillment === 'Partially Met';
            const obs    = (me.general_observation || me.comment || '').trim();
            const req    = (me.me_text || me.requirement || me.standard_text || '').trim(); // what was required
            const gp     = (me.area_of_good_practice || '').trim();
            const isCrit = (me.criticality_of_observation||'').toLowerCase() === 'high';

            if (isBad && obs.length > 8 && !entry.gaps.find(g=>g.obs===obs)) {
                entry.gaps.push({
                    obs,
                    req:  req.length > 8 ? req : null,
                    fulfillment: me.fulfillment,
                    crit: isCrit,
                });
                facilityMap[fac].totalGaps++;
                if (isCrit) facilityMap[fac].totalCrit++;
            }
            if (gp.length > 5 && !entry.good.includes(gp)) {
                entry.good.push(gp);
                facilityMap[fac].totalGood++;
            }
        });
    });

    const sorted = Object.entries(facilityMap)
        .filter(([,f]) => f.totalGaps > 0 || f.totalGood > 0)
        .sort((a,b) => b[1].totalGaps - a[1].totalGaps);

    if (!sorted.length) return null;

    // Systemic gaps
    const gapIndex = {};
    sorted.forEach(([,fdata]) => {
        Object.entries(fdata.clauses).forEach(([code, data]) => {
            data.gaps.forEach(gap => {
                const key = `${code}::${gap.obs.slice(0,50).toLowerCase()}`;
                if (!gapIndex[key]) gapIndex[key] = { code, obs:gap.obs, req:gap.req, crit:gap.crit, count:0 };
                gapIndex[key].count++;
            });
        });
    });
    const systemic = Object.values(gapIndex).filter(g=>g.count>=3)
        .sort((a,b)=>b.count-a.count).slice(0,8);

    // Rough weight estimate for a clause's content, used only to balance the two
    // newspaper columns below. Doesn't need to be exact — just roughly proportional
    // to how many printed lines the block will take.
    const estimateClauseWeight = (cdata) => {
        let w = 24; // clause header row
        cdata.gaps.forEach(g => { w += 18 + (g.req ? g.req.length/1.3 : 0) + g.obs.length/1.3; });
        cdata.good.forEach(gp => { w += 14 + gp.length/1.3; });
        return w;
    };

    // Greedy balanced split: walk items in order, always appending the next one to
    // whichever column currently has less accumulated weight. Preserves original
    // order within each column, so it still reads top-to-bottom like a newspaper —
    // but because each column is rendered as a plain block (not CSS column-count),
    // it paginates reliably across multiple printed pages.
    const splitIntoColumns = (items, weightFn, numCols = 2) => {
        const cols = Array.from({ length: numCols }, () => ({ items: [], weight: 0 }));
        items.forEach(item => {
            const lightest = cols.reduce((a, b) => a.weight <= b.weight ? a : b);
            lightest.items.push(item);
            lightest.weight += weightFn(item);
        });
        return cols.map(c => c.items);
    };

    // Facility card — 1 column, structured gap cards
    const FacilityCard = ({ name, fdata }) => {
        const clausesWithData = clauses.filter(cl =>
            fdata.clauses[cl.code]?.gaps.length > 0 || fdata.clauses[cl.code]?.good.length > 0
        );
        if (!clausesWithData.length) return null;

        const columns = splitIntoColumns(
            clausesWithData,
            cl => estimateClauseWeight(fdata.clauses[cl.code]),
            2
        );

        // NOTE (print fix): removed breakInside/pageBreakInside 'avoid' on the clause
        // wrapper below. At national scale, facilities with many gaps (70+) produce a
        // clause block taller than a printed page; a print engine given "don't split
        // this" on a block that can't fit pushes the WHOLE block to the next page,
        // leaving the remainder of the previous page blank. Letting it flow naturally
        // fixes the blank-page issue. The FacilityCard header still keeps
        // pageBreakAfter:'avoid' so the header itself never gets orphaned alone.
        const renderClause = (cl) => {
            const cdata = fdata.clauses[cl.code];
            return (
                <div key={cl.code} style={{ marginBottom:6 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:3 }}>
                        <span style={{ background:cl.color, color:'#fff', padding:'1px 6px', borderRadius:3, fontSize:12, fontWeight:800, flexShrink:0 }}>{cl.code}</span>
                        <span style={{ fontSize:12, color:'#444', fontWeight:600 }}>{cl.title}</span>
                    </div>
                    {cdata.gaps.map((gap,gi) => (
                        <div key={gi} style={{ marginBottom:3, marginLeft:6, padding:'3px 7px', background:gap.crit?'#fff2f0':'#fffbe6', borderLeft:`3px solid ${gap.crit?'#f5222d':'#fa8c16'}`, borderRadius:'0 4px 4px 0' }}>
                            {gap.req && (
                                <div style={{ marginBottom:2 }}>
                                    <span style={{ fontSize:11, fontWeight:800, color:'#1f3864', textTransform:'uppercase' }}>Required: </span>
                                    <span style={{ fontSize:12, color:'#444', lineHeight:1.4 }}>{gap.req}</span>
                                </div>
                            )}
                            <div>
                                <span style={{ fontSize:11, fontWeight:800, color:gap.fulfillment==='Not Met'?'#f5222d':'#fa8c16', textTransform:'uppercase' }}>
                                    {gap.fulfillment==='Not Met'?'✗ Gap: ':'◑ Partial: '}
                                </span>
                                <span style={{ fontSize:12, color:'#333', lineHeight:1.4 }}>{gap.obs}</span>
                                {gap.crit && <span style={{ marginLeft:4, background:'#f5222d', color:'#fff', fontSize:10, padding:'1px 3px', borderRadius:2, fontWeight:700 }}>HIGH</span>}
                            </div>
                        </div>
                    ))}
                    {cdata.good.map((gp,gi) => (
                        <div key={gi} style={{ marginBottom:3, marginLeft:6, padding:'3px 7px', background:'#f6ffed', borderLeft:'3px solid #52c41a', borderRadius:'0 4px 4px 0' }}>
                            <span style={{ fontSize:11, fontWeight:800, color:'#389e0d', textTransform:'uppercase' }}>✓ Good: </span>
                            <span style={{ fontSize:12, color:'#389e0d', fontStyle:'italic', lineHeight:1.4 }}>{gp}</span>
                        </div>
                    ))}
                </div>
            );
        };

        return (
            <div style={{ border:'1px solid #e0eaff', borderRadius:7, overflow:'hidden', marginBottom:10 }}>
                {/* Facility header — kept from being orphaned alone at a page bottom, but the
                    card as a whole is allowed to span pages since large facilities (100+ gaps)
                    are routinely taller than a single printed page. */}
                <div style={{ background:'#1f3864', padding:'5px 10px', display:'flex', alignItems:'center', justifyContent:'space-between', pageBreakAfter:'avoid', breakAfter:'avoid' }}>
                    <span style={{ fontWeight:800, color:'#fff', fontSize:13 }}>{name}</span>
                    <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                        {fdata.totalCrit > 0 && <span style={{ background:'#f5222d', color:'#fff', fontSize:12, padding:'1px 6px', borderRadius:3, fontWeight:700 }}>⚠ {fdata.totalCrit} critical</span>}
                        <span style={{ background:'rgba(255,255,255,0.15)', color:'#fff', fontSize:12, padding:'1px 6px', borderRadius:3 }}>{fdata.totalGaps} gap{fdata.totalGaps!==1?'s':''}</span>
                        {fdata.totalGood > 0 && <span style={{ background:'#52c41a', color:'#fff', fontSize:12, padding:'1px 6px', borderRadius:3 }}>✓ {fdata.totalGood} good</span>}
                    </div>
                </div>

                {/* Two independent flowing columns (not CSS column-count) — each one is a
                    normal block that paginates the ordinary, reliable way. Balanced by
                    estimated weight above so it still reads like a newspaper layout. */}
                <div style={{ padding:'6px 10px', background:'#fafbff', display:'flex', gap:12, alignItems:'flex-start' }}>
                    {columns.map((col, ci) => (
                        <div key={ci} style={{ flex:1, minWidth:0 }}>
                            {col.map(cl => renderClause(cl))}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div>
            {/* Systemic gaps */}
            {systemic.length > 0 && (
                <div style={{ marginBottom:12, padding:'10px 14px', background:'#fff2f0', border:'1px solid #ffccc7', borderLeft:'4px solid #f5222d', borderRadius:7, pageBreakInside:'avoid' }}>
                    <div style={{ fontWeight:800, fontSize:15, color:'#cf1322', marginBottom:8 }}>
                        🔴 SYSTEMIC GAPS — Identified Across 3+ Facilities
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                        {systemic.map((g,i) => {
                            const cl = CLAUSES.find(c=>c.code===g.code);
                            return (
                                <div key={i} style={{ display:'flex', gap:6, alignItems:'flex-start', fontSize:12, lineHeight:1.5, padding:'4px 6px', background:'#fff', borderRadius:4, border:'1px solid #ffccc7' }}>
                                    <span style={{ background:cl?.color||'#f5222d', color:'#fff', padding:'1px 5px', borderRadius:3, fontSize:11, fontWeight:800, flexShrink:0 }}>{g.code}</span>
                                    <span style={{ color:'#333', flex:1 }}>{g.obs.length>100?g.obs.slice(0,100)+'…':g.obs}</span>
                                    <span style={{ color:'#f5222d', fontWeight:700, fontSize:12, flexShrink:0 }}>{g.count}×</span>
                                </div>
                            );
                        })}
                    </div>
                    <ChartNote>Includes only gaps documented in identical or near-identical wording across at least three separate facilities.</ChartNote>
                </div>
            )}

            {/* Section header */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8, pageBreakAfter:'avoid' }}>
                <div style={{ fontWeight:800, fontSize:15, color:'#1f3864' }}>
                    FACILITY-SPECIFIC IMPROVEMENT AREAS ({sorted.length} facilities)
                </div>
                <div style={{ fontSize:12, color:'#888' }}>
                    Required = what the standard expects · Gap/Partial = what was observed
                </div>
            </div>

            {/* One facility per row — full width, clauses inside are 2-column */}
            {sorted.map(([name,fdata]) => (
                <FacilityCard key={name} name={name} fdata={fdata}/>
            ))}
        </div>
    );
};


// ── Table of Contents ─────────────────────────────────────────────────────────
const TableOfContents = ({ facilities, meResponses, hasQI, region, isNational }) => {
    // Real count of clauses that actually have response data for this
    // report — not the fixed list of every clause TYPE the platform
    // defines. meResponses is keyed by clause code, one array per clause;
    // an empty array means the key exists but nothing was actually scored.
    const clausesAssessedCount = meResponses
        ? Object.keys(meResponses).filter(code => (meResponses[code]||[]).length > 0).length
        : 0;

    const hasMe = meResponses && Object.keys(meResponses).length > 0;
    const hasFacilityGaps = hasMe && Object.values(meResponses).some(mes =>
        mes.some(m => (m.fulfillment==='Not Met'||m.fulfillment==='Partially Met') && (m.general_observation||m.comment||'').trim().length > 8)
    );

    const sections = [
        { num:'1', title: isNational ? 'Executive Summary' : 'Executive Summary', sub: `Overview of ${isNational?'national':region+' region'} QoC performance` },
        { num:'2', title: isNational ? 'Findings by Facility Type' : 'Findings & Discussions', sub: `Performance scores for ${facilities.length} facilities` },
        { num:'3', title: 'Quality Index', sub: 'QoC (80%) + Customer Survey (20%) composite score', show: hasQI },
        { num:'4', title: isNational ? 'National Performance Heatmap' : 'Facility Performance Heatmap', sub: 'Clause-by-clause score matrix' },
        { num:'5', title: 'Clause Performance Summary', sub: `${clausesAssessedCount} clauses assessed against standards` },
        { num:'6', title: 'Clause-by-Clause Analysis', sub: 'Detailed findings per clause with narratives' },
        { num:'7', title: 'Auditor Comments & Qualitative Findings', sub: 'Systemic gaps and facility-specific improvement areas', show: hasMe },
        { num:'8', title: 'Recommendations & Conclusion', sub: 'Priority actions and closing remarks' },
    ].filter(s => s.show !== false);

    return (
        // Border applied here because the TOC is forced onto its own single page via
        // pageBreakBefore:'always' plus minHeight/maxHeight pinning it to one A4 page —
        // same reasoning as CoverPage above. This does not generalize to the flowing
        // report-section blocks elsewhere in the report.
        <div className="report-section" style={{ padding:'28px 40px', pageBreakBefore:'always', minHeight:'100vh', maxHeight:'297mm', boxSizing:'border-box', border:'2px solid #1f3864' }}>
            {/* TOC Header */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, paddingBottom:10, borderBottom:'3px solid #1f3864' }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:'#1f3864', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:20, flexShrink:0 }}>≡</div>
                <div>
                    <div style={{ fontSize:24, fontWeight:900, color:'#1f3864' }}>TABLE OF CONTENTS</div>
                    <div style={{ fontSize:13, color:'#888' }}>{isNational ? 'National Quality of Care Report' : `${region} Region Quality of Care Report`}</div>
                </div>
            </div>

            {/* TOC entries */}
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <tbody>
                    {sections.map((s, i) => (
                        <tr key={i} style={{ borderBottom:'1px solid #f0f0f0' }}>
                            <td style={{ padding:'8px 0', width:32, verticalAlign:'top' }}>
                                <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:24, height:24, borderRadius:'50%', background:'#1f3864', color:'#fff', fontSize:13, fontWeight:800, flexShrink:0 }}>{s.num}</span>
                            </td>
                            <td style={{ padding:'8px 10px', verticalAlign:'top' }}>
                                <div style={{ fontWeight:700, fontSize:16, color:'#1f3864' }}>{s.title}</div>
                                <div style={{ fontSize:13, color:'#888', marginTop:2 }}>{s.sub}</div>
                            </td>
                            <td style={{ padding:'8px 0', textAlign:'right', verticalAlign:'middle', width:80 }}>
                                {/* Dot leader */}
                                <span style={{ borderBottom:'1px dotted #bbb', display:'inline-block', width:60, marginBottom:4 }}/>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Quick stats strip */}
            <div style={{ marginTop:20, display:'flex', gap:10, flexWrap:'wrap' }}>
                {[
                    { label:'Facilities', val:facilities.length, color:'#1f3864' },
                    { label:'Clauses Assessed', val:clausesAssessedCount, color:'#1890ff' },
                    { label:'Facility Gaps Documented', val: hasFacilityGaps ? Object.keys(meResponses).reduce((acc,code) => acc + (meResponses[code]||[]).filter(m=>(m.fulfillment==='Not Met'||m.fulfillment==='Partially Met')&&(m.general_observation||'').trim().length>8).length, 0) : 0, color:'#f5222d' },
                    { label:'Quality Index Records', val: hasQI ? facilities.filter(f=>f.qi&&parseFloat(f.qi.quality_index||0)>0).length : 0, color:'#7c3aed' },
                ].map(k => (
                    <div key={k.label} style={{ flex:1, minWidth:80, background:'#f8faff', border:'1px solid #e0eaff', borderLeft:`3px solid ${k.color}`, borderRadius:6, padding:'8px 12px' }}>
                        <div style={{ fontSize:12, color:'#888', textTransform:'uppercase', letterSpacing:0.5 }}>{k.label}</div>
                        <div style={{ fontSize:24, fontWeight:900, color:k.color }}>{k.val}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ── Cover Page ────────────────────────────────────────────────────────────────
const CoverPage = ({ title, subtitle, period, scope, facilityCount }) => (
    // Border applied here (not on flowing report-section pages) because this component
    // is guaranteed to occupy exactly one physical page (minHeight:100vh + maxHeight:297mm),
    // so a border drawn on it lines up with the printed page edge. Flowing sections can't
    // do this reliably since the browser print engine — not this component — decides where
    // page breaks fall for continuous content.
    <div className="report-page" style={{ padding:'0', background:'linear-gradient(160deg,#1f3864 0%,#2d6fa8 70%,rgba(24,144,255,0.2) 100%)', minHeight:'100vh', maxHeight:'297mm', boxSizing:'border-box', border:'2px solid rgba(255,255,255,0.35)', display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
        {/* Top band */}
        <div style={{ background:'rgba(0,0,0,0.2)', padding:'16px 48px', display:'flex', alignItems:'center', gap:16 }}>
            <img src={coa}  alt="Coat of Arms" style={{ height:48, filter:'brightness(10) drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}/>
            <img src={flag} alt="Flag"         style={{ height:32, borderRadius:3, boxShadow:'0 2px 8px rgba(0,0,0,0.3)' }}/>
            <div style={{ color:'rgba(255,255,255,0.85)', fontSize:15, lineHeight:1.5 }}>
                <div style={{ fontWeight:700, letterSpacing:1 }}>KINGDOM OF ESWATINI</div>
                <div style={{ opacity:0.7 }}>Ministry of Health · National Quality Management Programme</div>
            </div>
        </div>

        {/* Main content */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', textAlign:'center', padding:'40px 80px' }}>
            <div style={{ color:'rgba(255,255,255,0.6)', fontSize:15, letterSpacing:3, textTransform:'uppercase', marginBottom:24 }}>
                Eswatini Quality of Care Standards
            </div>
            <div style={{ width:60, height:4, background:'#1890ff', margin:'0 auto 28px', borderRadius:2 }}/>
            <div style={{ color:'#fff', fontSize:46, fontWeight:900, lineHeight:1.1, marginBottom:8, letterSpacing:1 }}>{title}</div>
            <div style={{ color:'rgba(255,255,255,0.8)', fontSize:22, fontWeight:600, marginBottom:40 }}>{subtitle}</div>
            <div style={{ display:'flex', gap:24, justifyContent:'center', flexWrap:'wrap' }}>
                {[
                    { label:'Period',     val: period },
                    { label:'Scope',      val: scope },
                    { label:'Facilities', val: facilityCount },
                    { label:'Generated',  val: moment().format('DD MMMM YYYY') },
                ].map(k=>(
                    <div key={k.label} style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'14px 22px', textAlign:'center', backdropFilter:'blur(8px)' }}>
                        <div style={{ color:'rgba(255,255,255,0.55)', fontSize:12, textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{k.label}</div>
                        <div style={{ color:'#fff', fontSize:20, fontWeight:800 }}>{k.val}</div>
                    </div>
                ))}
            </div>
        </div>

        {/* Bottom band */}
        <div style={{ background:'rgba(0,0,0,0.2)', padding:'12px 48px', textAlign:'center', color:'rgba(255,255,255,0.4)', fontSize:12 }}>
            QoC Platform · Eswatini Ministry of Health · {moment().format('YYYY')}
        </div>
    </div>
);

// ── Shared section styles ─────────────────────────────────────────────────────
const cell = { border:'1px solid #ddd', padding:'6px 8px', fontSize:15 };
const hdr  = { ...cell, background:'#1f3864', color:'#fff', fontWeight:700 };

// ── NATIONAL REPORT (all regions) ────────────────────────────────────────────
function NationalReport({ data, meResponses={}, enabledSections=new Set(SECTION_DEFS.map(s=>s.id)), enabledClauses=new Set(CLAUSES.map(c=>c.code)), isCustomized=false, comparisonPeriod=null, comparisonScores=null }) {
    const { period_label, facilities } = data;

    const overallAvg  = avg(facilities.map(f=>getFacScore(f)));
    const overallR    = getRating(overallAvg);
    const above70     = facilities.filter(f=>getFacScore(f)>=70).length;
    const below70     = facilities.length - above70;
    const ratingCount = stars => facilities.filter(f=>getRating(getFacScore(f)).stars===stars).length;

    // Group by region
    const regionStats = REGION_LIST.map(r=>{
        const facs   = facilities.filter(f=>(f.located_region||f.region||'').toLowerCase().includes(r.toLowerCase()));
        const ravg   = facs.length ? avg(facs.map(f=>getFacScore(f))) : 0;
        return { region:r, facs, avg:ravg, color:REGION_COLORS[r], r:getRating(ravg), above70:facs.filter(f=>getFacScore(f)>=70).length };
    }).filter(r=>r.facs.length>0);

    const bestRegion  = regionStats.length ? regionStats.reduce((a,b)=>a.avg>b.avg?a:b) : null;
    const worstRegion = regionStats.length ? regionStats.reduce((a,b)=>a.avg<b.avg?a:b) : null;

    // Group by type
    const hospitals  = facilities.filter(f=>(f.site_type||f.qi?.site_type||'').toLowerCase().includes('hospital'));
    const healthCtrs = facilities.filter(f=>(f.site_type||f.qi?.site_type||'').toLowerCase().includes('health centre')||(f.site_type||f.qi?.site_type||'').toLowerCase().includes('health center'));
    const clinics    = facilities.filter(f=>!hospitals.includes(f)&&!healthCtrs.includes(f));

    // Clause data — only iterate clauses the user has left enabled, so a
    // deselected clause disappears entirely from tables/heatmaps rather than
    // just showing an empty/zeroed row or column.
    const activeClauses = CLAUSES.filter(c => enabledClauses.has(c.code));
    const clauseData = activeClauses.map(cl=>{
        const scores = facilities.map(f=>parseFloat(f.clause_scores?.[cl.code]||0)).filter(s=>s>0);
        if (!scores.length) return null;
        const cavg = avg(scores);
        return { ...cl, cavg, above70:scores.filter(s=>s>=70).length, below70:scores.filter(s=>s<70).length, total:scores.length, r:getRating(cavg) };
    }).filter(Boolean);

    const topClause = clauseData.length ? clauseData.reduce((a,b)=>a.cavg>b.cavg?a:b) : null;
    const botClause = clauseData.length ? clauseData.reduce((a,b)=>a.cavg<b.cavg?a:b) : null;

    // National Quality Index aggregate — lifted up to this scope (rather than
    // computed inline inside the Quality Index section further down) so it's
    // also available for the pre-map summary cards. One computation, used in
    // both places, so the two can never drift apart from each other.
    const qiWithData = facilities.filter(f=>f.qi&&parseFloat(f.qi.quality_index||0)>0);
    const natAvgQI   = qiWithData.length ? avg(qiWithData.map(f=>parseFloat(f.qi.quality_index||0)))    : 0;
    const natAvgQoC  = qiWithData.length ? avg(qiWithData.map(f=>parseFloat(f.qi.qoc_score||0)))        : 0;
    const natAvgSrv  = qiWithData.length ? avg(qiWithData.map(f=>parseFloat(f.qi.survey_composite||0))) : 0;
    const natQIRating = getRating(natAvgQI);

    return (
        <div id="qoc-report-root" style={{ fontFamily:"'Segoe UI',Arial,sans-serif", color:'#222', background:'#fff' }}>

            {/* Cover page scope text derives from regionStats, which is already
                computed from the LIVE, currently-filtered `facilities` array
                above — not from a load-time snapshot. That matters: if the
                user deselects every facility in a region via the Customize
                drawer, regionStats naturally drops that region, and the scope
                text below reflects that immediately rather than continuing to
                claim "All Regions" from whatever was selected at load time. */}
            {enabledSections.has('coverPage') && (
                <CoverPage
                    title="QUALITY OF CARE"
                    subtitle={`Comprehensive National Audit Report${data.facType&&data.facType!=='all'?' — '+data.facType.charAt(0).toUpperCase()+data.facType.slice(1)+'s':''}${isCustomized?' (Customized Selection)':''}`}
                    period={period_label}
                    scope={`${regionStats.length < REGION_LIST.length ? regionStats.map(rs=>rs.region).join(', ') + (regionStats.length>1?' Regions':' Region') : 'National — All Regions'}${data.facType&&data.facType!=='all'?' · '+data.facType.charAt(0).toUpperCase()+data.facType.slice(1)+'s':''}`}
                    facilityCount={facilities.length}
                />
            )}
            {enabledSections.has('toc') && (
                <TableOfContents facilities={facilities} meResponses={meResponses} hasQI={data.qiItems&&data.qiItems.length>0} region="National" isNational={true}/>
            )}

            {/* Executive Summary */}
            {enabledSections.has('executiveSummary') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>EXECUTIVE SUMMARY</SectionBanner>

                {/* Hero stat — national average front and center, EPI-deck style,
                    ahead of the boxed Key Findings and narrative paragraphs. */}
                <HeroStat stats={[
                    { label:'National Average', value:`${overallAvg.toFixed(1)}%`, color:safeColor(overallR.color), sub:overallR.label },
                    { label:'Facilities Audited', value:facilities.length, color:'#1f3864' },
                    { label:'Meeting 70% Threshold', value:`${above70}/${facilities.length}`, color:below70>0?'#fa8c16':'#389e0d' },
                ]}/>

                {/* Key Findings — bullet-first summary, EPI-deck boxed style with
                    checkmark bullets, ahead of the narrative paragraphs that follow. */}
                <KeyFindings variant="boxed" color={overallR.color} items={[
                    <>Audits covered <strong>{facilities.length} healthcare facilities</strong> nationally during <strong>{period_label}</strong>, across all ten QoC standard clauses and all four regions.</>,
                    <>The national average score was <strong style={{color:safeColor(overallR.color)}}>{overallAvg.toFixed(1)}%</strong> ({overallR.label}).</>,
                    above70 > 0 ? <><strong>{above70} of {facilities.length} facilities ({Math.round(above70/facilities.length*100)}%)</strong> met the 70% minimum threshold.</> : null,
                    below70 > 0 ? <><strong>{below70} facilit{below70>1?'ies':'y'}</strong> scored below 70% and require urgent support.</> : null,
                    bestRegion && worstRegion && bestRegion.region !== worstRegion.region
                        ? <><strong style={{color:safeColor(bestRegion.color)}}>{bestRegion.region}</strong> led nationally at {bestRegion.avg.toFixed(1)}%; <strong style={{color:safeColor(worstRegion.color)}}>{worstRegion.region}</strong> trailed at {worstRegion.avg.toFixed(1)}%.</>
                        : null,
                    topClause && botClause && topClause.code !== botClause.code
                        ? <>Strongest clause: <strong style={{color:safeColor(topClause.color)}}>{topClause.code}</strong> ({topClause.cavg.toFixed(1)}%). Most improvement needed: <strong style={{color:safeColor(botClause.color)}}>{botClause.code}</strong> ({botClause.cavg.toFixed(1)}%).</>
                        : null,
                ]}/>

                <p style={{ fontSize:16, lineHeight:1.9, marginBottom:6 }}>
                    The Ministry of Health through the National Quality Management Programme conducted quality of care audits across <strong>{facilities.length} healthcare facilities</strong> nationally during <strong>{period_label}</strong>. The audit evaluated all ten (10) QoC standard clauses across all four regions.
                </p>
                <p style={{ fontSize:16, lineHeight:1.9, marginBottom:6 }}>
                    The national average score was <strong style={{ color:safeColor(overallR.color) }}>{overallAvg.toFixed(1)}%</strong> ({overallR.label}).
                    {above70 > 0 && ` ${above70} of ${facilities.length} facilities (${Math.round(above70/facilities.length*100)}%) met the 70% minimum threshold.`}
                    {below70 > 0 && ` ${below70} facilit${below70>1?'ies':'y'} scored below 70% and require urgent support.`}
                </p>
                {bestRegion && worstRegion && bestRegion.region !== worstRegion.region && (
                    <p style={{ fontSize:16, lineHeight:1.9, marginBottom:6 }}>
                        <strong style={{ color:safeColor(bestRegion.color) }}>{bestRegion.region} Region</strong> led nationally with an average of {bestRegion.avg.toFixed(1)}%, while <strong style={{ color:safeColor(worstRegion.color) }}>{worstRegion.region} Region</strong> recorded the lowest at {worstRegion.avg.toFixed(1)}%. The {(bestRegion.avg-worstRegion.avg).toFixed(1)} percentage point gap highlights regional disparities that require targeted interventions.
                    </p>
                )}
                {topClause && botClause && topClause.code !== botClause.code && (
                    <p style={{ fontSize:16, lineHeight:1.9, marginBottom:8 }}>
                        Nationally, the strongest clause was <strong style={{ color:safeColor(topClause.color) }}>{topClause.code} — {topClause.title}</strong> at {topClause.cavg.toFixed(1)}%, while <strong style={{ color:safeColor(botClause.color) }}>{botClause.code}</strong> requires most attention at {botClause.cavg.toFixed(1)}%.
                    </p>
                )}
                <SourceLine>QoC Assessment records, approved submissions only, {period_label}.</SourceLine>
            </div>
            )}

            {/* Star Rating — split out of Executive Summary so it can be shown/hidden
                on its own, independent of the narrative paragraphs above it. */}
            {enabledSections.has('starRating') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <div style={{ fontWeight:700, fontSize:17, color:'#1f3864', marginBottom:8 }}>NATIONAL STAR RATING DISTRIBUTION</div>
                <KeyFindings variant="boxed" color="#1f3864" items={[
                    <>Rating bands run from <strong>Inadequate</strong> (below 70%) to <strong>Outstanding</strong> (98% and above).</>,
                    <><strong>{ratingCount(0)}</strong> facilit{ratingCount(0)!==1?'ies':'y'} {ratingCount(0)!==1?'fall':'falls'} in the Inadequate band and need the most urgent support.</>,
                    <><strong>{ratingCount(4)+ratingCount(5)}</strong> facilit{(ratingCount(4)+ratingCount(5))!==1?'ies':'y'} {(ratingCount(4)+ratingCount(5))!==1?'achieve':'achieves'} Excellent or Outstanding ratings (95%+).</>,
                ]}/>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:15, marginBottom:10 }}>
                    <thead><tr>{['Score Range','Interpretation','Stars','Facilities'].map(h=><th key={h} style={{ ...hdr, padding:'7px 10px' }}>{h}</th>)}</tr></thead>
                    <tbody>
                        {RATING.map((r,i)=>(
                            <tr key={i}>
                                <td style={{ ...cell, background:r.rowBg, color:r.rowText, fontWeight:700 }}>{r.min}–{r.max}%</td>
                                <td style={{ ...cell, background:r.rowBg, color:r.rowText, fontWeight:700 }}>{r.label}</td>
                                <td style={{ ...cell, fontSize:17 }}>{'★'.repeat(r.stars)||'—'}</td>
                                <td style={{ ...cell, fontWeight:700, textAlign:'center' }}>{ratingCount(r.stars)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <ChartNote>Rating bands follow the national QoC standard: Outstanding 98–100%, Excellent 95–97.9%, Good 90–94.9%, Fair 85–89.9%, Requires Improvement 70–84.9%, Inadequate below 70%.</ChartNote>

                {/* Region comparison cards */}
                <div style={{ fontWeight:700, fontSize:17, color:'#1f3864', marginBottom:10, marginTop:16 }}>REGIONAL COMPARISON</div>
                <div style={{ display:'flex', gap:12, marginBottom:6, flexWrap:'wrap' }}>
                    {regionStats.map(rs=>(
                        <div key={rs.region} style={{ flex:1, minWidth:110, background:'#f8faff', border:`2px solid ${rs.color}`, borderRadius:10, padding:'12px', textAlign:'center' }}>
                            <div style={{ fontSize:15, fontWeight:700, color:rs.color, marginBottom:2 }}>{rs.region}</div>
                            <div style={{ fontSize:13, color:'#aaa', marginBottom:4 }}>{rs.facs.length} facilities</div>
                            <div style={{ fontSize:26, fontWeight:900, color:safeColor(rs.r.color) }}>{rs.avg.toFixed(1)}%</div>
                            <div style={{ fontSize:13, color:safeColor(rs.r.color) }}>{rs.r.label}</div>
                            <div style={{ fontSize:13 }}>{'★'.repeat(rs.r.stars)||'—'}</div>
                            <div style={{ fontSize:12, color:'#555', marginTop:4 }}>{rs.above70}/{rs.facs.length} above 70%</div>
                        </div>
                    ))}
                </div>
                <SourceLine>QoC Assessment records, {period_label}.</SourceLine>
            </div>
            )}

            {/* National Aggregate Cards — sits ABOVE the map, per request. Both
                strips reuse values already computed at the top of this
                function (clauseData/topClause/botClause for the clause card,
                natAvgQI/natAvgQoC/natAvgSrv for the QI card) rather than
                recomputing them — the detailed Clause Performance Summary and
                Quality Index sections further down use the exact same
                numbers, so nothing can drift between the summary shown here
                and the detail shown later. */}
            {enabledSections.has('nationalAggregates') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>NATIONAL PERFORMANCE AT A GLANCE</SectionBanner>
                {clauseData.length > 0 && (() => {
                    const natClauseAvg = avg(clauseData.map(c=>c.cavg));
                    const natR = getRating(natClauseAvg);
                    const above70Count = clauseData.filter(c=>c.cavg>=70).length;
                    return (
                        <>
                            <div style={{ fontSize:15, fontWeight:700, color:'#888', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>Clause Performance</div>
                            <SummaryCardStrip cards={[
                                { label:'National Clause Average', val:`${natClauseAvg.toFixed(1)}%`, color:natR.color },
                                { label:'Best Clause', val:topClause?.code||'—', color:topClause?.color, sub:topClause?`${topClause.cavg.toFixed(1)}%`:null },
                                { label:'Weakest Clause', val:botClause?.code||'—', color:botClause?.color, sub:botClause?`${botClause.cavg.toFixed(1)}%`:null },
                                { label:'Clauses ≥70%', val:`${above70Count}/${clauseData.length}`, color:'#389e0d' },
                                { label:'Clauses <70%', val:`${clauseData.length-above70Count}/${clauseData.length}`, color:'#f5222d' },
                            ]}/>
                        </>
                    );
                })()}
                <div style={{ fontSize:15, fontWeight:700, color:'#888', marginBottom:6, marginTop:4, textTransform:'uppercase', letterSpacing:0.5 }}>Quality Index</div>
                <SummaryCardStrip cards={[
                    { label:'National Avg Quality Index', val:`${natAvgQI.toFixed(1)}%`, color:safeColor(natQIRating.color) },
                    { label:'Avg QoC Score (80%)', val:`${natAvgQoC.toFixed(1)}%`, color:'#1890ff' },
                    { label:'Avg Survey Score (20%)', val:`${natAvgSrv.toFixed(1)}%`, color:'#52c41a' },
                    { label:'Facilities with QI', val:qiWithData.length, color:'#333' },
                    { label:'Pending Calculation', val:facilities.length-qiWithData.length, color:'#fa8c16' },
                ]}/>
                <ChartNote>Quality Index = QoC assessment score (80% weight) + customer satisfaction survey score (20% weight). Facilities without survey data are scored on QoC alone.</ChartNote>
            </div>
            )}

            {/* Regional Performance Map — fed from regionStats, which is already
                computed from the live, currently-selected facility set, so a
                facility/clause selection that drops a region drops it from the
                map too, same as everywhere else in this report. */}
            {enabledSections.has('regionalMap') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>REGIONAL PERFORMANCE MAP</SectionBanner>
                <KeyFindings items={[
                    bestRegion && <>The map is shaded by average score: darker regions reflect lower relative performance among the {regionStats.length} region{regionStats.length!==1?'s':''} shown.</>,
                    bestRegion && worstRegion && bestRegion.region !== worstRegion.region
                        ? <><strong style={{color:safeColor(bestRegion.color)}}>{bestRegion.region}</strong> ({bestRegion.avg.toFixed(1)}%) and <strong style={{color:safeColor(worstRegion.color)}}>{worstRegion.region}</strong> ({worstRegion.avg.toFixed(1)}%) mark the current national range.</>
                        : null,
                ]}/>
                <RegionMap regionStats={regionStats}/>
                <ChartNote>Facility counts per region reflect only facilities currently selected in the Customize drawer.</ChartNote>
                <SourceLine>QoC Assessment records, {period_label}.</SourceLine>
            </div>
            )}

            {/* National Heatmap — moved up: performance-analysis sections
                (Heatmap, Clause Performance Summary, Quality Index, Facility
                Detail, Period Comparison) now come right after the map,
                ahead of the narrative Findings/Regional Deep-Dive sections. */}
            {enabledSections.has('heatmap') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>NATIONAL PERFORMANCE HEATMAP</SectionBanner>
                <div style={{ fontSize:15, color:'#666', marginBottom:10 }}>All {facilities.length} facilities across all regions. Colours represent star rating bands.</div>
                <RatingLegend/>
                <Heatmap facilities={facilities} clauses={activeClauses}/>
                <ChartNote>Cells show each facility's score per clause; the bottom row is the unweighted regional average across facilities with a non-zero score for that clause.</ChartNote>
            </div>
            )}

            {/* Clause Performance Summary */}
            {enabledSections.has('clausePerformanceSummary') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>CLAUSE PERFORMANCE SUMMARY</SectionBanner>
                <KeyFindings items={[
                    topClause && <>Strongest performing clause nationally: <strong style={{color:safeColor(topClause.color)}}>{topClause.code} — {topClause.title}</strong> at <strong>{topClause.cavg.toFixed(1)}%</strong>.</>,
                    botClause && <>Weakest performing clause nationally: <strong style={{color:safeColor(botClause.color)}}>{botClause.code} — {botClause.title}</strong> at <strong>{botClause.cavg.toFixed(1)}%</strong>.</>,
                    <><strong>{clauseData.filter(c=>c.cavg>=70).length} of {clauseData.length}</strong> assessed clauses average 70% or above nationally.</>,
                ]}/>
                <ClauseRadarBar clauseData={clauseData}/>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:15 }}>
                    <thead>
                        <tr style={{ background:'#f0f5ff' }}>
                            {['Clause','National Avg','Rating','≥70%','<70%','Performance'].map(h=>(
                                <th key={h} style={{ padding:'7px 10px', textAlign:h==='Clause'?'left':'center', borderBottom:'2px solid #d6e4ff' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {clauseData.map((cl,i)=>(
                            <tr key={cl.code} style={{ background:i%2?'#fafafa':'#fff' }}>
                                <td style={{ padding:'7px 10px' }}><span style={{ fontWeight:800, color:cl.color, marginRight:6 }}>{cl.code}</span><span style={{ fontSize:13, color:'#555' }}>{cl.title}</span></td>
                                <td style={{ padding:'7px 10px', textAlign:'center', fontWeight:800, fontSize:17, color:safeColor(cl.r.color) }}>{cl.cavg.toFixed(1)}%</td>
                                <td style={{ padding:'7px 10px', textAlign:'center' }}><span style={{ color:safeColor(cl.r.color), fontWeight:700, fontSize:13 }}>{cl.r.label}</span></td>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#389e0d', fontWeight:700 }}>{cl.above70}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#f5222d', fontWeight:700 }}>{cl.below70}</td>
                                <td style={{ padding:'7px 10px' }}>
                                    <div style={{ background:'#f0f0f0', borderRadius:4, height:14, overflow:'hidden' }}>
                                        <div style={{ width:`${Math.min(cl.cavg,100)}%`, height:'100%', background:cl.r.color, borderRadius:4 }}/>
                                    </div>
                                </td>
                            </tr>

                        ))}
                    </tbody>
                </table>
                <ChartNote>Averages include only facilities with a non-zero score for that clause; facilities not yet assessed on a clause are excluded rather than counted as zero.</ChartNote>

                {/* ME clause narratives */}
                {meResponses && Object.keys(meResponses).length > 0 && (
                    <div style={{ marginTop:16 }}>
                        <div style={{ fontWeight:700, fontSize:15, color:'#1f3864', marginBottom:8, paddingLeft:4 }}>QUALITATIVE ANALYSIS BY CLAUSE</div>
                        {clauseData.map(cl => {
                            const narrative = buildClauseNarrative(meResponses, cl.code, cl.title,
                                facilities.map(f=>parseFloat(f.clause_scores?.[cl.code]||0)).filter(s=>s>0));
                            if (!narrative) return null;
                            const ins = getMEInsights(meResponses, cl.code);
                            return (
                                <div key={cl.code} style={{ marginBottom:10, padding:'8px 12px', background:'#f8faff', borderRadius:6, borderLeft:`3px solid ${cl.color}` }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                                        <span style={{ fontWeight:800, color:cl.color, fontSize:15 }}>{cl.code}</span>
                                        {ins?.criticalGaps?.length > 0 && <span style={{ background:'#fff2f0', color:'#f5222d', fontSize:12, padding:'1px 5px', borderRadius:3, fontWeight:700 }}>⚠ {ins.criticalGaps.length} critical gap{ins.criticalGaps.length>1?'s':''}</span>}
                                        {ins?.goodPractices?.length > 0 && <span style={{ background:'#f6ffed', color:'#389e0d', fontSize:12, padding:'1px 5px', borderRadius:3, fontWeight:700 }}>✓ Good practice</span>}
                                    </div>
                                    <p style={{ fontSize:15, lineHeight:1.8, color:'#444', margin:0, fontStyle:'italic' }}>{narrative}</p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            )}

            {enabledSections.has('facilityDetail') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>FACILITY DETAIL</SectionBanner>
                <div style={{ fontSize:15, color:'#666', marginBottom:10 }}>{facilities.length} facilities, sorted by overall score.</div>
                <FacilityDetailTable facilities={facilities} meResponses={meResponses} clauses={activeClauses} showRegion={true}/>
                <ChartNote>"Best/Weakest Clause" reflect only clauses currently enabled in the Customize drawer; a facility with no scored clauses shows "—".</ChartNote>
            </div>
            )}
            {/* National Quality Index */}
            {enabledSections.has('qualityIndex') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>QUALITY INDEX — QoC Score (80%) + Customer Survey Score (20%)</SectionBanner>
                {(() => {
                    // withQI/avgQI/etc. reuse the lifted qiWithData/natAvgQI/natAvgQoC/
                    // natAvgSrv computed at the top of this function (same values
                    // shown in the National Aggregate Cards above the map) — kept as
                    // local aliases here purely so the rest of this block reads the
                    // same as before, not because they're recomputed.
                    const withQI     = qiWithData;
                    const noSurvey   = withQI.filter(f=>parseFloat(f.qi?.survey_composite||0)===0).length;
                    const hasSurvey  = withQI.length - noSurvey;
                    const hospitals  = facilities.filter(f=>(f.site_type||f.qi?.site_type||'').toLowerCase().includes('hospital'));
                    const healthCtrs = facilities.filter(f=>(f.site_type||f.qi?.site_type||'').toLowerCase().includes('health centre')||(f.site_type||f.qi?.site_type||'').toLowerCase().includes('health center'));
                    const clinics    = facilities.filter(f=>!hospitals.includes(f)&&!healthCtrs.includes(f));
                    return (
                        <>
                            <KeyFindings items={[
                                <><strong>{withQI.length} of {facilities.length}</strong> facilities have a calculated Quality Index for {period_label}.</>,
                                <>National average Quality Index: <strong style={{color:safeColor(natQIRating.color)}}>{natAvgQI.toFixed(1)}%</strong> ({natQIRating.label}).</>,
                                noSurvey > 0 ? <><strong>{noSurvey}</strong> facilit{noSurvey>1?'ies':'y'} {noSurvey>1?'have':'has'} no customer survey data and are scored on QoC alone.</> : null,
                            ]}/>
                            <p style={{ fontSize:16, lineHeight:1.9, marginBottom:12, color:'#555' }}>
                                The Quality Index combines the QoC assessment score (weighted at 80%) with the customer satisfaction survey score (weighted at 20%), providing a holistic measure of healthcare quality incorporating both clinical standards compliance and patient experience.
                                {noSurvey > 0 && ` Note: ${noSurvey} of ${withQI.length} facilit${noSurvey>1?'ies':'y'} have no customer survey data for this period — their Quality Index reflects QoC scores only (effectively 80% weight).`}
                                {hasSurvey > 0 && ` ${hasSurvey} facilit${hasSurvey>1?'ies':'y'} have both QoC and survey data.`}
                            </p>

                            {/* Quality Index by Region — the gap this closes: QI previously
                                only broke down by facility type, with no regional view at
                                all. Built from regionStats.facs, which is already the live,
                                currently-selected facility set — same source the map and
                                Regional Deep-Dive use, so this stays in sync with facility
                                and clause selection automatically. */}
                            {regionStats.length > 0 && (() => {
                                const regionQI = regionStats.map(rs => {
                                    const withQIr = rs.facs.filter(f=>f.qi&&parseFloat(f.qi.quality_index||0)>0);
                                    const rAvgQI  = withQIr.length ? avg(withQIr.map(f=>parseFloat(f.qi.quality_index||0)))    : 0;
                                    const rAvgQoC = withQIr.length ? avg(withQIr.map(f=>parseFloat(f.qi.qoc_score||0)))        : 0;
                                    const rAvgSrv = withQIr.length ? avg(withQIr.map(f=>parseFloat(f.qi.survey_composite||0))) : 0;
                                    return { region:rs.region, color:rs.color, withQICount:withQIr.length, total:rs.facs.length, rAvgQI, rAvgQoC, rAvgSrv };
                                });
                                return (
                                    <div style={{ marginBottom:16 }}>
                                        <div style={{ fontWeight:700, fontSize:16, color:'#1f3864', marginBottom:8 }}>QUALITY INDEX BY REGION</div>
                                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:15 }}>
                                            <thead>
                                                <tr style={{ background:'#1f3864' }}>
                                                    {['Region','Facilities with QI','Avg QoC (80%)','Avg Survey (20%)','Quality Index','Rating'].map(h=>(
                                                        <th key={h} style={{ padding:'6px 8px', color:'#fff', fontWeight:700, textAlign:h==='Region'?'left':'center', border:'1px solid #ddd' }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {regionQI.map((rq,i) => {
                                                    const rt = getRating(rq.rAvgQI);
                                                    return (
                                                        <tr key={rq.region} style={{ background:i%2?'#fafafa':'#fff' }}>
                                                            <td style={{ padding:'5px 8px', fontWeight:700, color:rq.color, border:'1px solid #eee' }}>{rq.region}</td>
                                                            <td style={{ padding:'5px 8px', textAlign:'center', border:'1px solid #eee' }}>{rq.withQICount} / {rq.total}</td>
                                                            <td style={{ padding:'5px 8px', textAlign:'center', color:'#1890ff', fontWeight:700, border:'1px solid #eee' }}>{rq.rAvgQoC.toFixed(1)}%</td>
                                                            <td style={{ padding:'5px 8px', textAlign:'center', color:'#52c41a', fontWeight:700, border:'1px solid #eee' }}>{rq.rAvgSrv.toFixed(1)}%</td>
                                                            <td style={{ padding:'5px 8px', textAlign:'center', fontWeight:900, fontSize:17, color:safeColor(rt.color), border:'1px solid #eee' }}>{rq.rAvgQI.toFixed(1)}%</td>
                                                            <td style={{ padding:'5px 8px', textAlign:'center', border:'1px solid #eee' }}>
                                                                <span style={{ background:rt.rowBg, color:rt.rowText, padding:'2px 6px', borderRadius:4, fontSize:12, fontWeight:700 }}>{rt.label}</span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}

                            {hospitals.length>0  && <QualityIndexTable facilities={hospitals}  title={`Hospitals (${hospitals.length})`}/>}
                            {healthCtrs.length>0 && <QualityIndexTable facilities={healthCtrs} title={`Health Centres (${healthCtrs.length})`}/>}
                            {clinics.length>0    && <QualityIndexTable facilities={clinics}    title={`Clinics & PHUs (${clinics.length})`}/>}
                            {withQI.length===0 && (
                                <div style={{padding:24,textAlign:'center',color:'#fa8c16',background:'#fff7e6',borderRadius:8,border:'1px solid #ffd591'}}>
                                    ⚠ No Quality Index records found for this period and scope. Use the Quality Index page to calculate scores first.
                                </div>
                            )}
                            <ChartNote>Quality Index = (QoC score × 80%) + (customer survey score × 20%). Facilities with no survey data are scored on QoC alone.</ChartNote>
                        </>
                    );
                })()}
            </div>
            )}

            {/* Period Comparison — compares the current period's clause scores
                (already filtered to the current facility/clause selection)
                against a second period, chosen and fetched via the Customize
                drawer. The comparison data itself is fetched server-side with
                the same facility/clause filters, so both sides of this chart
                reflect the same scope. Renders nothing if no comparison
                period has been picked yet. */}
            {enabledSections.has('periodComparison') && comparisonPeriod && comparisonScores && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>PERIOD COMPARISON</SectionBanner>
                <div style={{ fontSize:15, color:'#666', marginBottom:10 }}>{period_label} vs {comparisonPeriod} — same facility and clause selection applied to both periods.</div>
                <PeriodComparisonChart currentClauseData={clauseData} currentLabel={period_label} comparisonScores={comparisonScores} comparisonLabel={comparisonPeriod}/>
                <ChartNote>Both periods are filtered to the same facility and clause selection, so the comparison reflects a like-for-like scope rather than each period's full unfiltered dataset.</ChartNote>
            </div>
            )}

            {/* Findings by type */}
            {enabledSections.has('findingsByType') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>FINDINGS BY FACILITY TYPE</SectionBanner>

                {[
                    { label:'Hospitals', items:hospitals, color:'#0d6e6e' },
                    { label:'Health Centres', items:healthCtrs, color:'#5b2d8e' },
                    { label:'Clinics & PHUs', items:clinics, color:'#c0392b' },
                ].filter(g=>g.items.length>0).map(group=>{
                    const gavg   = avg(group.items.map(f=>getFacScore(f)));
                    const gr     = getRating(gavg);
                    const gAbove = group.items.filter(f=>getFacScore(f)>=70).length;
                    const gBelow = group.items.length - gAbove;
                    const top    = [...group.items].sort((a,b)=>getFacScore(b)-getFacScore(a))[0];
                    const bot    = [...group.items].sort((a,b)=>getFacScore(a)-getFacScore(b))[0];
                    return (
                        <div key={group.label} style={{ marginBottom:24 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
                                <div style={{ background:group.color, color:'#fff', borderRadius:6, padding:'3px 14px', fontWeight:800, fontSize:16 }}>{group.label}</div>
                                <span style={{ fontSize:15, color:'#555' }}>{group.items.length} facilities · avg <strong style={{ color:safeColor(gr.color) }}>{gavg.toFixed(1)}%</strong> ({gr.label})</span>
                                {gBelow > 0 && <Tag color="red">{gBelow} below 70%</Tag>}
                            </div>
                            <p style={{ fontSize:15, lineHeight:1.8, color:'#444', marginBottom:8, background:`${group.color}0d`, padding:'8px 12px', borderRadius:6, borderLeft:`4px solid ${group.color}` }}>
                                The {group.items.length} {group.label.toLowerCase()} recorded an average of {gavg.toFixed(1)}% ({gr.label}). {gAbove} of {group.items.length} met the 70% threshold.
                                {top && ` Best performer: ${top.business_name} (${getFacScore(top).toFixed(1)}%).`}
                                {bot && gBelow > 0 && ` ${bot.business_name} (${getFacScore(bot).toFixed(1)}%) requires urgent support.`}
                            </p>
                            <BarChart items={group.items} showRegion={true}/>
                        </div>
                    );
                })}
                <SourceLine>QoC Assessment records, {period_label}.</SourceLine>
            </div>
            )}

            {/* Regional deep-dive */}
            {enabledSections.has('regionalDeepDive') && regionStats.map((rs, ri) => (
                <div key={rs.region} className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                        <div style={{ background:rs.color, color:'#fff', borderRadius:8, padding:'4px 16px', fontWeight:900, fontSize:17 }}>{rs.region}</div>
                        <div style={{ fontSize:20, fontWeight:900, color:'#1f3864' }}>Regional Performance</div>
                        <div style={{ marginLeft:'auto', fontSize:17, fontWeight:700, color:safeColor(rs.r.color) }}>{rs.avg.toFixed(1)}% — {rs.r.label}</div>
                    </div>
                    <div style={{ height:3, background:`linear-gradient(90deg,${rs.color},transparent)`, marginBottom:14, borderRadius:2 }}/>
                    <p style={{ fontSize:15, lineHeight:1.85, color:'#333', marginBottom:10, background:`${rs.color}0d`, padding:'10px 14px', borderRadius:6, borderLeft:`4px solid ${rs.color}` }}>
                        The {rs.region} region had {rs.facs.length} facilit{rs.facs.length>1?'ies':'y'} assessed, recording a regional average of {rs.avg.toFixed(1)}% ({rs.r.label}). {rs.above70} of {rs.facs.length} facilities met the 70% minimum threshold{rs.facs.length-rs.above70>0?`, while ${rs.facs.length-rs.above70} require targeted improvement`:''}.
                    </p>
                    <BarChart items={rs.facs}/>

                    {/* Clause mini-scorecard */}
                    {(() => {
                        const clauseRows = activeClauses.map(cl=>{
                            const scores = rs.facs.map(f=>parseFloat(f.clause_scores?.[cl.code]||0)).filter(s=>s>0);
                            if (!scores.length) return null;
                            const cavg = avg(scores);
                            return { ...cl, cavg, cr:getRating(cavg) };
                        }).filter(Boolean);
                        if (!clauseRows.length) return null;
                        return (
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5, marginTop:10 }}>
                                <thead>
                                    <tr style={{ background:'#1f3864' }}>
                                        {['Code','Clause Title','Avg Score','Rating'].map(h=>(
                                            <th key={h} style={{ padding:'4px 8px', color:'#fff', fontWeight:700, textAlign:'left', border:'1px solid #aaa' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {clauseRows.map((cl,ci)=>(
                                        <tr key={cl.code} style={{ background:ci%2?'#f5f5f5':'#fff' }}>
                                            <td style={{ padding:'3px 8px', border:'1px solid #ddd', fontWeight:700, color:cl.color }}>{cl.code}</td>
                                            <td style={{ padding:'3px 8px', border:'1px solid #ddd' }}>{cl.title}</td>
                                            <td style={{ padding:'3px 8px', border:'1px solid #ddd', fontWeight:700, color:safeColor(cl.cr.color), textAlign:'center' }}>{cl.cavg.toFixed(1)}%</td>
                                            <td style={{ padding:'3px 8px', border:'1px solid #ddd', color:safeColor(cl.cr.color) }}>{'★'.repeat(cl.cr.stars)||'—'} {cl.cr.label}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        );
                    })()}
                </div>
            ))}

            {/* National Recommendations — previously only existed on the Regional
                report. Mirrors that structure, scoped to national-level figures
                (bestRegion/worstRegion, national clauseData) instead of a single
                region's numbers. */}
            {enabledSections.has('recommendations') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>RECOMMENDATIONS &amp; CONCLUSION</SectionBanner>
                {(() => {
                    const above80National = facilities.filter(f=>getFacScore(f)>=80).length;
                    const nationalRecs = [];
                    const facilityRecs = [];
                    if (below70>0) { nationalRecs.push(`Prioritise targeted national support for the ${below70} facilit${below70>1?'ies':'y'} scoring below 70%`); facilityRecs.push('Develop structured quality improvement plans with measurable targets and timelines'); }
                    if (above80National>0) { nationalRecs.push(`Document and disseminate best practices from the ${above80National} high-performing facilit${above80National>1?'ies':'y'} nationally`); }
                    if (bestRegion && worstRegion && bestRegion.region !== worstRegion.region) {
                        nationalRecs.push(`Direct additional oversight and resources to ${worstRegion.region} Region, which trails ${bestRegion.region} Region by ${(bestRegion.avg-worstRegion.avg).toFixed(1)} points`);
                    }
                    clauseData.filter(cl=>cl.cavg<70).slice(0,3).forEach(cl=>{ facilityRecs.push(`Strengthen ${cl.code}: ${cl.below70} facilit${cl.below70>1?'ies':'y'} nationally below 70% require focused improvement`); nationalRecs.push(`Conduct nationwide targeted training on ${cl.code} — ${cl.title} standards`); });
                    clauseData.filter(cl=>cl.cavg>=85).slice(0,2).forEach(cl=>{ facilityRecs.push(`Sustain ${cl.code} performance (${cl.cavg.toFixed(1)}%) and document as model practice`); });
                    nationalRecs.push('Implement quarterly national performance review meetings across regions');
                    facilityRecs.push('Review and update outdated SOPs to align with national guidelines');
                    facilityRecs.push('Strengthen quality improvement committees with documented meetings and follow-up');
                    const maxRows = Math.max(nationalRecs.length, facilityRecs.length);
                    return (
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:15, marginBottom:10 }}>
                            <thead><tr>{['National Level','Facility Level'].map(h=><th key={h} style={{ ...hdr, padding:'8px 12px' }}>{h}</th>)}</tr></thead>
                            <tbody>
                                {Array.from({length:maxRows},(_,i)=>[nationalRecs[i]||'',facilityRecs[i]||'']).map((row,i)=>(
                                    <tr key={i} style={{ background:i%2?'#f5f5f5':'#fff' }}>
                                        {row.map((c,j)=><td key={j} style={{ ...cell, verticalAlign:'top' }}>{c}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    );
                })()}
                <p style={{ fontSize:16, lineHeight:1.9 }}>
                    The national audit of <strong>{facilities.length} facilities</strong> yielded a national average of <strong style={{ color:safeColor(overallR.color) }}>{overallAvg.toFixed(1)}%</strong> ({overallR.label}).
                    {above70>0&&` ${above70} facilit${above70>1?'ies':'y'} met the 70% threshold.`}
                    {below70>0&&` ${below70} facilit${below70>1?'ies':'y'} require focused improvement.`}
                    {topClause&&` Best national performance in ${topClause.code} (${topClause.cavg.toFixed(1)}%)${botClause&&botClause.code!==topClause.code?`; most improvement needed in ${botClause.code} (${botClause.cavg.toFixed(1)}%)`:''}.`}
                    {` Structured corrective action plans supported by national oversight will be critical to sustained improvement.`}
                </p>
            </div>
            )}

            {/* National Qualitative Comments — deliberately the LAST section in
                the report, per request: performance-analysis sections moved up
                near the top, qualitative/narrative findings now close the
                report out instead of sitting in the middle of it. */}
            {enabledSections.has('qualitativeComments') && meResponses && Object.keys(meResponses).length > 0 && (
                <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                    <SectionBanner>AUDITOR COMMENTS &amp; QUALITATIVE FINDINGS</SectionBanner>

                    <QualitativeComments meResponses={meResponses} facilities={facilities} clauses={activeClauses}/>
                </div>
            )}
        </div>
    );
}

// ── REGIONAL REPORT (single region) ──────────────────────────────────────────
function RegionalReport({ data, meResponses={}, enabledSections=new Set(SECTION_DEFS.map(s=>s.id)), enabledClauses=new Set(CLAUSES.map(c=>c.code)), isCustomized=false, comparisonPeriod=null, comparisonScores=null }) {
    const { region, period_label, facilities } = data;
    const regionColor = REGION_COLORS[region] || '#1f3864';
    const overallAvg  = avg(facilities.map(f=>getFacScore(f)));
    const regionR     = getRating(overallAvg);
    const above70     = facilities.filter(f=>getFacScore(f)>=70).length;
    const below70     = facilities.length - above70;
    const above80     = facilities.filter(f=>getFacScore(f)>=80).length;
    const sorted      = [...facilities].sort((a,b)=>getFacScore(b)-getFacScore(a));
    const top3        = sorted.slice(0,3);
    const bot3        = sorted.filter(f=>getFacScore(f)<70).slice(-3);
    const ratingCount = stars => facilities.filter(f=>getRating(getFacScore(f)).stars===stars).length;

    const activeClauses = CLAUSES.filter(c => enabledClauses.has(c.code));
    const clauseData = activeClauses.map(cl=>{
        const scores = facilities.map(f=>parseFloat(f.clause_scores?.[cl.code]||0)).filter(s=>s>0);
        if (!scores.length) return null;
        const cavg = avg(scores);
        return { ...cl, cavg, above70:scores.filter(s=>s>=70).length, below70:scores.filter(s=>s<70).length, total:scores.length, r:getRating(cavg) };
    }).filter(Boolean);
    const topClause = clauseData.length ? clauseData.reduce((a,b)=>a.cavg>b.cavg?a:b) : null;
    const botClause = clauseData.length ? clauseData.reduce((a,b)=>a.cavg<b.cavg?a:b) : null;

    return (
        <div id="qoc-report-root" style={{ fontFamily:"'Segoe UI',Arial,sans-serif", color:'#222', background:'#fff' }}>

            {enabledSections.has('coverPage') && (
                <CoverPage
                    title={`${region.toUpperCase()} REGION`}
                    subtitle={`Quality of Care Regional Audit Report${data.facType&&data.facType!=='all'?' — '+data.facType.charAt(0).toUpperCase()+data.facType.slice(1)+'s':''}${isCustomized?' (Customized Selection)':''}`}
                    period={period_label}
                    scope={`${region} Region${data.facType&&data.facType!=='all'?' · '+data.facType.charAt(0).toUpperCase()+data.facType.slice(1)+'s':''}`}
                    facilityCount={facilities.length}
                />
            )}
            {enabledSections.has('toc') && (
                <TableOfContents facilities={facilities} meResponses={meResponses} hasQI={data.qiItems&&data.qiItems.length>0} region={region} isNational={false}/>
            )}

            {/* Executive Summary */}
            {enabledSections.has('executiveSummary') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>EXECUTIVE SUMMARY</SectionBanner>

                {/* Hero stat — regional average front and center, EPI-deck style. */}
                <HeroStat stats={[
                    { label:`${region} Average`, value:`${overallAvg.toFixed(1)}%`, color:safeColor(regionR.color), sub:regionR.label },
                    { label:'Facilities Audited', value:facilities.length, color:regionColor },
                    { label:'Meeting 70% Threshold', value:`${above70}/${facilities.length}`, color:below70>0?'#fa8c16':'#389e0d' },
                ]}/>

                <KeyFindings variant="boxed" color={safeColor(regionR.color)} items={[
                    <>Audits covered <strong>{facilities.length} healthcare facilities</strong> in the <strong style={{color:regionColor}}>{region} Region</strong> during <strong>{period_label}</strong>.</>,
                    <>The regional average was <strong style={{color:safeColor(regionR.color)}}>{overallAvg.toFixed(1)}%</strong> ({regionR.label}).</>,
                    above70 > 0 ? <><strong>{above70} of {facilities.length} facilities ({Math.round(above70/facilities.length*100)}%)</strong> met the 70% minimum threshold.</> : null,
                    below70 > 0 ? <><strong>{below70} facilit{below70>1?'ies':'y'}</strong> scored below 70% and require urgent support.</> : null,
                    topClause && botClause && topClause.code !== botClause.code
                        ? <>Strongest clause: <strong style={{color:safeColor(topClause.color)}}>{topClause.code}</strong> ({topClause.cavg.toFixed(1)}%). Most improvement needed: <strong style={{color:safeColor(botClause.color)}}>{botClause.code}</strong> ({botClause.cavg.toFixed(1)}%).</>
                        : null,
                ]}/>
                <p style={{ fontSize:16, lineHeight:1.9, marginBottom:6 }}>
                    The Ministry of Health conducted quality of care audits across <strong>{facilities.length} healthcare facilities</strong> in the <strong>{region} Region</strong> during <strong>{period_label}</strong>.
                    The regional average was <strong style={{ color:safeColor(regionR.color) }}>{overallAvg.toFixed(1)}%</strong> ({regionR.label}).
                    {above70 > 0 && ` ${above70} of ${facilities.length} facilities (${Math.round(above70/facilities.length*100)}%) met the 70% minimum threshold.`}
                    {below70 > 0 && ` ${below70} facilit${below70>1?'ies':'y'} scored below 70% and require urgent support.`}
                </p>
                {topClause && botClause && topClause.code !== botClause.code && (
                    <p style={{ fontSize:16, lineHeight:1.9, marginBottom:6 }}>
                        Strongest clause: <strong style={{ color:safeColor(topClause.color) }}>{topClause.code} — {topClause.title}</strong> at {topClause.cavg.toFixed(1)}%. Most improvement needed in <strong style={{ color:safeColor(botClause.color) }}>{botClause.code}</strong> at {botClause.cavg.toFixed(1)}%.
                    </p>
                )}

                <Row gutter={16}>
                    <Col span={12}>
                        <div style={{ background:'#f6ffed', border:'1px solid #b7eb8f', borderLeft:'4px solid #52c41a', borderRadius:6, padding:'12px 14px' }}>
                            <div style={{ fontWeight:700, fontSize:16, color:'#389e0d', marginBottom:8 }}>BEST PERFORMING</div>
                            {top3.map(f=>{
                                const r = getRating(getFacScore(f));
                                return <div key={f.id} style={{ display:'flex', justifyContent:'space-between', marginBottom:5, fontSize:15 }}><span>{f.business_name}</span><strong style={{ color:safeColor(r.color) }}>{getFacScore(f).toFixed(1)}%</strong></div>;
                            })}
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ background:'#fff2f0', border:'1px solid #ffccc7', borderLeft:'4px solid #f5222d', borderRadius:6, padding:'12px 14px' }}>
                            <div style={{ fontWeight:700, fontSize:16, color:'#cf1322', marginBottom:8 }}>⚠ NEEDING SUPPORT</div>
                            {bot3.length > 0 ? bot3.map(f=>{
                                const r = getRating(getFacScore(f));
                                return <div key={f.id} style={{ display:'flex', justifyContent:'space-between', marginBottom:5, fontSize:15 }}><span>{f.business_name}</span><strong style={{ color:safeColor(r.color) }}>{getFacScore(f).toFixed(1)}%</strong></div>;
                            }) : <div style={{ fontSize:15, color:'#389e0d' }}>✓ All facilities above 70%</div>}
                        </div>
                    </Col>
                </Row>
                <SourceLine>QoC Assessment records, approved submissions only, {period_label}.</SourceLine>
            </div>
            )}

            {/* Star Rating — split out of Executive Summary so it can be shown/hidden
                on its own, independent of the narrative and best/worst cards above it. */}
            {enabledSections.has('starRating') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <div style={{ fontWeight:700, fontSize:17, color:'#1f3864', marginBottom:8 }}>STAR RATING DISTRIBUTION</div>
                <KeyFindings variant="boxed" color="#1f3864" items={[
                    <><strong>{ratingCount(0)}</strong> facilit{ratingCount(0)!==1?'ies':'y'} in {region} {ratingCount(0)!==1?'fall':'falls'} in the Inadequate band (below 70%).</>,
                    <><strong>{ratingCount(4)+ratingCount(5)}</strong> facilit{(ratingCount(4)+ratingCount(5))!==1?'ies':'y'} {(ratingCount(4)+ratingCount(5))!==1?'achieve':'achieves'} Excellent or Outstanding ratings (95%+).</>,
                ]}/>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:15, marginBottom:10 }}>
                    <thead><tr>{['Score Range','Interpretation','Stars','Facilities'].map(h=><th key={h} style={{ ...hdr, padding:'7px 10px' }}>{h}</th>)}</tr></thead>
                    <tbody>
                        {RATING.map((r,i)=>(
                            <tr key={i}>
                                <td style={{ ...cell, background:r.rowBg, color:r.rowText, fontWeight:700 }}>{r.min}–{r.max}%</td>
                                <td style={{ ...cell, background:r.rowBg, color:r.rowText, fontWeight:700 }}>{r.label}</td>
                                <td style={{ ...cell, fontSize:17 }}>{'★'.repeat(r.stars)||'—'}</td>
                                <td style={{ ...cell, fontWeight:700, textAlign:'center' }}>{ratingCount(r.stars)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <ChartNote>Rating bands follow the national QoC standard: Outstanding 98–100%, Excellent 95–97.9%, Good 90–94.9%, Fair 85–89.9%, Requires Improvement 70–84.9%, Inadequate below 70%.</ChartNote>
            </div>
            )}

            {/* Facility Performance Heatmap — moved up: performance-analysis
                sections (Heatmap, Clause Performance Summary, Quality Index,
                Facility Detail, Period Comparison, Clause-by-Clause detail)
                now come right after Star Rating, ahead of the narrative
                Findings & Discussions section. */}
            {enabledSections.has('heatmap') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>FACILITY PERFORMANCE HEATMAP</SectionBanner>
                <RatingLegend/>
                <Heatmap facilities={facilities} clauses={activeClauses}/>
                <ChartNote>Cells show each facility's score per clause; the bottom row is the unweighted regional average across facilities with a non-zero score for that clause.</ChartNote>
            </div>
            )}

            {/* Clause Performance Summary */}
            {enabledSections.has('clausePerformanceSummary') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>CLAUSE PERFORMANCE SUMMARY</SectionBanner>
                <div style={{ background:`linear-gradient(135deg,${regionColor},${regionColor}cc)`, borderRadius:10, padding:'14px 20px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                    <div>
                        <div style={{ fontSize:13, color:'rgba(255,255,255,0.7)', fontWeight:700, letterSpacing:1 }}>REGIONAL AVERAGE</div>
                        <div style={{ fontSize:32, fontWeight:900, color:'#fff' }}>{overallAvg.toFixed(1)}%</div>
                        <div style={{ fontSize:15, color:'rgba(255,255,255,0.85)' }}>{regionR.label} · {facilities.length} facilities</div>
                    </div>
                    <Progress type="circle" percent={Math.min(100,overallAvg)} width={70} strokeColor="#fff" trailColor="rgba(255,255,255,0.3)"
                        format={()=><span style={{fontWeight:900,color:'#fff',fontSize:17}}>{overallAvg.toFixed(0)}%</span>}/>
                </div>
                <KeyFindings items={[
                    topClause && <>Strongest clause in {region}: <strong style={{color:safeColor(topClause.color)}}>{topClause.code} — {topClause.title}</strong> at <strong>{topClause.cavg.toFixed(1)}%</strong>.</>,
                    botClause && <>Weakest clause in {region}: <strong style={{color:safeColor(botClause.color)}}>{botClause.code} — {botClause.title}</strong> at <strong>{botClause.cavg.toFixed(1)}%</strong>.</>,
                ]}/>
                <ClauseRadarBar clauseData={clauseData}/>
                {clauseData.length > 0 && (() => {
                    const regClauseAvg = avg(clauseData.map(c=>c.cavg));
                    const regR = getRating(regClauseAvg);
                    const above70Count = clauseData.filter(c=>c.cavg>=70).length;
                    return (
                        <SummaryCardStrip cards={[
                            { label:'Regional Clause Average', val:`${regClauseAvg.toFixed(1)}%`, color:regR.color },
                            { label:'Best Clause', val:topClause?.code||'—', color:topClause?.color, sub:topClause?`${topClause.cavg.toFixed(1)}%`:null },
                            { label:'Weakest Clause', val:botClause?.code||'—', color:botClause?.color, sub:botClause?`${botClause.cavg.toFixed(1)}%`:null },
                            { label:'Clauses ≥70%', val:`${above70Count}/${clauseData.length}`, color:'#389e0d' },
                            { label:'Clauses <70%', val:`${clauseData.length-above70Count}/${clauseData.length}`, color:'#f5222d' },
                        ]}/>
                    );
                })()}
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:15 }}>
                    <thead>
                        <tr style={{ background:'#f0f5ff' }}>
                            {['Clause','Avg Score','Rating','≥70%','<70%','Performance'].map(h=>(
                                <th key={h} style={{ padding:'7px 10px', textAlign:h==='Clause'?'left':'center', borderBottom:'2px solid #d6e4ff' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {clauseData.map((cl,i)=>(
                            <tr key={cl.code} style={{ background:i%2?'#fafafa':'#fff' }}>
                                <td style={{ padding:'7px 10px' }}><span style={{ fontWeight:800, color:cl.color, marginRight:6 }}>{cl.code}</span><span style={{ fontSize:13, color:'#555' }}>{cl.title}</span></td>
                                <td style={{ padding:'7px 10px', textAlign:'center', fontWeight:800, fontSize:17, color:safeColor(cl.r.color) }}>{cl.cavg.toFixed(1)}%</td>
                                <td style={{ padding:'7px 10px', textAlign:'center' }}><span style={{ color:safeColor(cl.r.color), fontWeight:700, fontSize:13 }}>{cl.r.label}</span></td>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#389e0d', fontWeight:700 }}>{cl.above70}</td>
                                <td style={{ padding:'7px 10px', textAlign:'center', color:'#f5222d', fontWeight:700 }}>{cl.below70}</td>
                                <td style={{ padding:'7px 10px' }}><div style={{ background:'#f0f0f0', borderRadius:4, height:14, overflow:'hidden' }}><div style={{ width:`${Math.min(cl.cavg,100)}%`, height:'100%', background:cl.r.color, borderRadius:4 }}/></div></td>
                            </tr>

                        ))}
                    </tbody>
                </table>
                <ChartNote>Averages include only facilities with a non-zero score for that clause.</ChartNote>

                {/* ME clause narratives */}
                {meResponses && Object.keys(meResponses).length > 0 && (
                    <div style={{ marginTop:16 }}>
                        <div style={{ fontWeight:700, fontSize:15, color:'#1f3864', marginBottom:8, paddingLeft:4 }}>QUALITATIVE ANALYSIS BY CLAUSE</div>
                        {clauseData.map(cl => {
                            const narrative = buildClauseNarrative(meResponses, cl.code, cl.title,
                                facilities.map(f=>parseFloat(f.clause_scores?.[cl.code]||0)).filter(s=>s>0));
                            if (!narrative) return null;
                            const ins = getMEInsights(meResponses, cl.code);
                            return (
                                <div key={cl.code} style={{ marginBottom:10, padding:'8px 12px', background:'#f8faff', borderRadius:6, borderLeft:`3px solid ${cl.color}` }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                                        <span style={{ fontWeight:800, color:cl.color, fontSize:15 }}>{cl.code}</span>
                                        {ins?.criticalGaps?.length > 0 && <span style={{ background:'#fff2f0', color:'#f5222d', fontSize:12, padding:'1px 5px', borderRadius:3, fontWeight:700 }}>⚠ {ins.criticalGaps.length} critical gap{ins.criticalGaps.length>1?'s':''}</span>}
                                        {ins?.goodPractices?.length > 0 && <span style={{ background:'#f6ffed', color:'#389e0d', fontSize:12, padding:'1px 5px', borderRadius:3, fontWeight:700 }}>✓ Good practice</span>}
                                    </div>
                                    <p style={{ fontSize:15, lineHeight:1.8, color:'#444', margin:0, fontStyle:'italic' }}>{narrative}</p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            )}

            {/* Facility Detail — summary table across every currently-selected
                facility in this region, respecting the clause selection for
                best/weakest clause and using meResponses for critical gaps. */}
            {enabledSections.has('facilityDetail') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>FACILITY DETAIL</SectionBanner>
                <div style={{ fontSize:15, color:'#666', marginBottom:10 }}>{facilities.length} facilities, sorted by overall score.</div>
                <FacilityDetailTable facilities={facilities} meResponses={meResponses} clauses={activeClauses} showRegion={false}/>
                <ChartNote>"Best/Weakest Clause" reflect only clauses currently enabled in the Customize drawer.</ChartNote>
            </div>
            )}

            {/* Regional Quality Index */}
            {enabledSections.has('qualityIndex') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>QUALITY INDEX — QoC Score (80%) + Customer Survey Score (20%)</SectionBanner>
                {(() => {
                    const withQI = facilities.filter(f=>f.qi&&parseFloat(f.qi.quality_index||0)>0);
                    const avgQI  = withQI.length ? avg(withQI.map(f=>parseFloat(f.qi.quality_index||0)))    : 0;
                    const avgQoC = withQI.length ? avg(withQI.map(f=>parseFloat(f.qi.qoc_score||0)))        : 0;
                    const avgSrv = withQI.length ? avg(withQI.map(f=>parseFloat(f.qi.survey_composite||0))) : 0;
                    const rqi    = getRating(avgQI);
                    return (
                        <>
                            <KeyFindings items={[
                                <><strong>{withQI.length} of {facilities.length}</strong> facilities in {region} have a calculated Quality Index for {period_label}.</>,
                                <>Regional average Quality Index: <strong style={{color:safeColor(rqi.color)}}>{avgQI.toFixed(1)}%</strong> ({rqi.label}).</>,
                            ]}/>
                            <SummaryCardStrip cards={[
                                { label:'Regional Avg Quality Index', val:`${avgQI.toFixed(1)}%`, color:safeColor(rqi.color) },
                                { label:'Avg QoC Score (80%)', val:`${avgQoC.toFixed(1)}%`, color:'#1890ff' },
                                { label:'Avg Survey Score (20%)', val:`${avgSrv.toFixed(1)}%`, color:'#52c41a' },
                                { label:'Facilities with QI', val:withQI.length, color:'#333' },
                                { label:'Pending Calculation', val:facilities.length-withQI.length, color:'#fa8c16' },
                            ]}/>
                        </>
                    );
                })()}
                <p style={{ fontSize:16, lineHeight:1.9, marginBottom:16, color:'#555' }}>
                    The Quality Index combines the QoC assessment score (weighted at 80%) with the customer satisfaction survey score (weighted at 20%), providing a holistic measure incorporating both clinical standards compliance and patient experience.
                </p>
                {/* By type */}
                {(() => {
                    const hospitals  = facilities.filter(f=>(f.site_type||f.qi?.site_type||'').toLowerCase().includes('hospital'));
                    const healthCtrs = facilities.filter(f=>(f.site_type||f.qi?.site_type||'').toLowerCase().includes('health centre')||(f.site_type||f.qi?.site_type||'').toLowerCase().includes('health center'));
                    const clinics    = facilities.filter(f=>!hospitals.includes(f)&&!healthCtrs.includes(f));
                    return (
                        <>
                            {hospitals.length>0  && <QualityIndexTable facilities={hospitals}  title={`Hospitals (${hospitals.length})`}/>}
                            {healthCtrs.length>0 && <QualityIndexTable facilities={healthCtrs} title={`Health Centres (${healthCtrs.length})`}/>}
                            {clinics.length>0    && <QualityIndexTable facilities={clinics}    title={`Clinics & PHUs (${clinics.length})`}/>}
                        </>
                    );
                })()}
                <ChartNote>Quality Index = (QoC score × 80%) + (customer survey score × 20%). Facilities with no survey data are scored on QoC alone.</ChartNote>
            </div>
            )}

            {/* Period Comparison — same mechanism as the National report: the
                comparison period's data is fetched server-side with the same
                facility/clause filters as the current period, so both sides
                of the chart reflect the same scope. */}
            {enabledSections.has('periodComparison') && comparisonPeriod && comparisonScores && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>PERIOD COMPARISON</SectionBanner>
                <div style={{ fontSize:15, color:'#666', marginBottom:10 }}>{period_label} vs {comparisonPeriod} — same facility and clause selection applied to both periods.</div>
                <PeriodComparisonChart currentClauseData={clauseData} currentLabel={period_label} comparisonScores={comparisonScores} comparisonLabel={comparisonPeriod}/>
                <ChartNote>Both periods are filtered to the same facility and clause selection.</ChartNote>
            </div>
            )}

            {/* Clause Summary */}
            {enabledSections.has('clauseByClauseSummary') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>CLAUSE-BY-CLAUSE SUMMARY</SectionBanner>
                {clauseData.map(cl=>{
                    const scores = facilities.map(f=>({ name:f.business_name, score:parseFloat(f.clause_scores?.[cl.code]||0) })).filter(f=>f.score>0).sort((a,b)=>b.score-a.score);
                    if (!scores.length) return null;
                    const top = scores[0]; const bot = scores[scores.length-1];
                    const above70c = scores.filter(s=>s.score>=70).length;
                    return (
                        <div key={cl.code} style={{ marginBottom:14, padding:'10px 14px', background:'#f8faff', borderRadius:8, borderLeft:`4px solid ${cl.color}`, pageBreakInside:'avoid' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                                <div><span style={{ fontWeight:900, color:cl.color, fontSize:16 }}>{cl.code}</span><span style={{ fontSize:15, color:'#333', marginLeft:8 }}>{cl.title}</span></div>
                                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                    <span style={{ fontWeight:800, fontSize:17, color:safeColor(cl.r.color) }}>{cl.cavg.toFixed(1)}%</span>
                                    <span style={{ background:cl.r.rowBg, color:cl.r.rowText, padding:'2px 8px', borderRadius:4, fontSize:13, fontWeight:700 }}>{cl.r.label}</span>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:6 }}>
                                {scores.map(f=>(
                                    <div key={f.name} style={{ display:'flex', alignItems:'center', gap:4, flex:'0 0 48%', marginBottom:2 }}>
                                        <div style={{ fontSize:12, color:'#555', width:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', textAlign:'right', flexShrink:0 }}>{f.name}</div>
                                        <div style={{ flex:1, background:'#e5e7eb', borderRadius:3, height:11 }}><div style={{ width:`${Math.min(f.score,100)}%`, height:'100%', background:getRating(f.score).color, borderRadius:3 }}/></div>
                                        <span style={{ fontSize:12, fontWeight:700, color:safeColor(getRating(f.score).color), width:32, textAlign:'right', flexShrink:0 }}>{f.score.toFixed(0)}%</span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ display:'flex', gap:16, fontSize:13, color:'#555' }}>
                                <span><strong style={{color:'#389e0d'}}>{above70c}</strong>/{scores.length} above 70%</span>
                                {top && <span>Best: <strong style={{color:'#389e0d'}}>{top.name}</strong> ({top.score.toFixed(1)}%)</span>}
                                {bot && bot.name!==top?.name && <span>Lowest: <strong style={{color:'#f5222d'}}>{bot.name}</strong> ({bot.score.toFixed(1)}%)</span>}
                            </div>
                        </div>
                    );
                })}
            </div>
            )}

            {/* Findings — moved down, after the performance-analysis cluster */}
            {enabledSections.has('findingsDiscussions') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>FINDINGS &amp; DISCUSSIONS</SectionBanner>
                <p style={{ fontSize:16, lineHeight:1.9, marginBottom:6 }}>
                    {facilities.length} healthcare facilities in {region} Region were assessed. The regional average of {overallAvg.toFixed(1)}% ({regionR.label}) {above80>0?`with ${above80} facilities above 80% reflecting commendable performance`:''}{below70>0?`. ${below70} facilit${below70>1?'ies':'y'} below 70% require structured corrective action plans`:''}.
                </p>
                <div style={{ fontSize:17, fontWeight:700, color:regionColor, marginBottom:8 }}>Overall Scores — {region} Region</div>
                <BarChart items={facilities}/>
                <SourceLine>QoC Assessment records, {period_label}.</SourceLine>
            </div>
            )}

            {/* Dynamic Recommendations */}
            {enabledSections.has('recommendations') && (
            <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                <SectionBanner>RECOMMENDATIONS &amp; CONCLUSION</SectionBanner>
                {(() => {
                    const regionalRecs = [];
                    const facilityRecs = [];
                    if (below70>0) { regionalRecs.push(`Prioritise targeted support for the ${below70} facilit${below70>1?'ies':'y'} scoring below 70%`); facilityRecs.push('Develop structured quality improvement plans with measurable targets and timelines'); }
                    if (above80>0) { regionalRecs.push(`Document and share best practices from the ${above80} high-performing facilit${above80>1?'ies':'y'} across the region`); }
                    clauseData.filter(cl=>cl.cavg<70).slice(0,3).forEach(cl=>{ facilityRecs.push(`Strengthen ${cl.code}: ${cl.below70} facilit${cl.below70>1?'ies':'y'} below 70% require focused improvement`); regionalRecs.push(`Conduct targeted training on ${cl.code} — ${cl.title} standards`); });
                    clauseData.filter(cl=>cl.cavg>=85).slice(0,2).forEach(cl=>{ facilityRecs.push(`Sustain ${cl.code} performance (${cl.cavg.toFixed(1)}%) and document as model practice`); });
                    regionalRecs.push('Implement monthly regional performance review meetings');
                    facilityRecs.push('Review and update outdated SOPs to align with national guidelines');
                    facilityRecs.push('Strengthen quality improvement committees with documented meetings and follow-up');
                    const maxRows = Math.max(regionalRecs.length, facilityRecs.length);
                    return (
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:15, marginBottom:10 }}>
                            <thead><tr>{['Regional Level','Facility Level'].map(h=><th key={h} style={{ ...hdr, padding:'8px 12px' }}>{h}</th>)}</tr></thead>
                            <tbody>
                                {Array.from({length:maxRows},(_,i)=>[regionalRecs[i]||'',facilityRecs[i]||'']).map((row,i)=>(
                                    <tr key={i} style={{ background:i%2?'#f5f5f5':'#fff' }}>
                                        {row.map((c,j)=><td key={j} style={{ ...cell, verticalAlign:'top' }}>{c}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    );
                })()}
                <p style={{ fontSize:16, lineHeight:1.9 }}>
                    The audit of <strong>{facilities.length} facilities</strong> in {region} Region yielded a regional average of <strong style={{ color:safeColor(regionR.color) }}>{overallAvg.toFixed(1)}%</strong> ({regionR.label}).
                    {above70>0&&` ${above70} facilit${above70>1?'ies':'y'} met the 70% threshold.`}
                    {below70>0&&` ${below70} facilit${below70>1?'ies':'y'} require focused improvement.`}
                    {topClause&&` Best performance in ${topClause.code} (${topClause.cavg.toFixed(1)}%)${botClause&&botClause.code!==topClause.code?`; most improvement needed in ${botClause.code} (${botClause.cavg.toFixed(1)}%)`:''}.`}
                    {` Structured corrective action plans supported by regional oversight will be critical to sustained improvement.`}
                </p>
            </div>
            )}

            {/* Qualitative Comments — deliberately the LAST section in the
                report, per request: performance-analysis sections moved up
                near the top, qualitative/narrative findings now close the
                report out instead of sitting in the middle of it. */}
            {enabledSections.has('qualitativeComments') && data.meResponses && Object.keys(data.meResponses).length > 0 && (
                <div className="report-section" style={{ padding:'12px 40px', marginBottom:8 }}>
                    <SectionBanner>AUDITOR COMMENTS &amp; QUALITATIVE FINDINGS</SectionBanner>

                    <QualitativeComments meResponses={data.meResponses} facilities={facilities} clauses={activeClauses}/>
                </div>
            )}
        </div>
    );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
export default function QoCRegionalReport({ onClose }) {
    const [periods, setPeriods] = useState([]);
    const [period,  setPeriod]  = useState('');
    // Multi-region filter — defaults to all four regions selected (equivalent
    // to the old single-select "all" option). Selecting fewer than all four
    // still renders via NationalReport (which naturally shows only the
    // regions present in the loaded data); selecting exactly one switches to
    // RegionalReport's single-region branding.
    const lockedRegion = util.getLockedRegion ? util.getLockedRegion() : null;
    const [selectedRegions, setSelectedRegions] = useState(lockedRegion ? [lockedRegion] : REGION_LIST);
    const [facType, setFacType] = useState('all');
    const [loading,   setLoading]   = useState(false);
    const [data,     setData]     = useState(null);
    const [meLoading, setMeLoading] = useState(false);

    // ── Report customization state ──────────────────────────────────────────
    // Which sections, clauses, and facilities are currently included in the
    // rendered report. All default to "everything on" and get reset to
    // "everything on" whenever a fresh load() succeeds, since a stale
    // selection referencing facility ids from a previous period/region could
    // silently produce an empty-looking report.
    const [customizeOpen, setCustomizeOpen] = useState(false);

    // Explicit Escape-key close, independent of antd Drawer's own `keyboard`
    // handling — a third fallback alongside the header X and the in-body
    // Close button, in case whatever is intercepting clicks on the header X
    // also interferes with antd's internal keydown handling.
    useEffect(() => {
        if (!customizeOpen) return;
        const onKeyDown = (e) => { if (e.key === 'Escape') setCustomizeOpen(false); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [customizeOpen]);
    const [enabledSections, setEnabledSections] = useState(() => new Set(SECTION_DEFS.map(s=>s.id)));
    const [enabledClauses, setEnabledClauses] = useState(() => new Set(CLAUSES.map(c=>c.code)));
    const [enabledFacilityIds, setEnabledFacilityIds] = useState(() => new Set());
    const [facilitySearch, setFacilitySearch] = useState('');

    // Quality Index numbers (qoc_score, survey_composite, quality_index) are NOT
    // something the client can safely recompute — they come from a weighted
    // formula plus survey data the client doesn't have loaded. So instead of
    // approximating them, we ask the backend to recalculate them for exactly
    // the facilities/clauses currently selected, and merge the result over the
    // original unfiltered qi objects. null = "no override yet, use original".
    const [qiOverride, setQiOverride] = useState(null);
    const [qiRecalculating, setQiRecalculating] = useState(false);

    // Period Comparison — a second period the user picks in the Customize
    // drawer to compare against the report's own period. comparisonScores is
    // a { clauseCode: avgScore } map fetched server-side with the SAME
    // facility_id/clause_code filters as the current selection, so the chart
    // compares apples to apples rather than a differently-scoped dataset.
    const [comparisonPeriod, setComparisonPeriod] = useState(null);
    const [comparisonScores, setComparisonScores] = useState(null);
    const [comparisonLoading, setComparisonLoading] = useState(false);

    useEffect(() => {
        QoCService.getPeriods().then(r => {
            const ps = r.data?.data || [];
            setPeriods(ps);
            const cur = ps.find(p => p.is_current == 1);
            if (cur) setPeriod(cur.label);
        }).catch(()=>{});
    }, []);

    const load = async () => {
        if (!period) { message.warning('Select a period'); return; }
        setLoading(true); setData(null);
        try {
            const [aRes, facRes, qiRes, compRes] = await Promise.all([
                QoCService.getAssessments({ status:'approved' }),
                FacilityService.all({ status:1 }),
                QoCService.getQualityIndex({ period_label: period }).catch(()=>({ data:{ data:[] } })),
                QoCService.getComparisonReport({ period_label: period }).catch(()=>({ data:{ data:[] } })),
            ]);
            const compRows = compRes.data?.data || [];
            const compMap  = Object.fromEntries(compRows.map(r=>[String(r.facility_id), r]));
            const allData = aRes.data?.data || [];
            const allFacs = facRes.data?.result?.data || [];
            const facMap  = Object.fromEntries(allFacs.map(f=>[String(f.id),f]));
            const periodData = allData.filter(a=>(a.period_label||'').trim()===period.trim());

            // Filter by selected regions. If every region is selected this is a
            // no-op (equivalent to the old "all"), so no special-case branch is
            // needed — a subset of regions falls out naturally.
            const isSingleRegionSelection = selectedRegions.length === 1;
            const regionFilteredData = selectedRegions.length === REGION_LIST.length
                ? periodData
                : periodData.filter(a => {
                    const fac = facMap[String(a.facility_id)];
                    const facRegion = (fac?.located_region || fac?.region || '').toLowerCase();
                    return fac && facRegion && selectedRegions.some(r => facRegion.includes(r.toLowerCase()));
                });

            if (!regionFilteredData.length) { message.warning(`No approved assessments found for ${period}${selectedRegions.length < REGION_LIST.length ? ` in ${selectedRegions.join(', ')}` : ''}`); setLoading(false); return; }

            const facIds = [...new Set(regionFilteredData.map(a=>a.facility_id))];
            const facilities = facIds.map(fid=>{
                const fac      = facMap[String(fid)] || { id:fid, business_name:'Unknown', site_type:'' };
                const facAsmts = regionFilteredData.filter(a=>a.facility_id===fid);
                const clauseScores = {};
                facAsmts.forEach(a=>{ if (a.clause_code) { const s=getFacScore(a); if (!clauseScores[a.clause_code]||s>clauseScores[a.clause_code]) clauseScores[a.clause_code]=s; }});
                const scores  = Object.values(clauseScores).filter(s=>s>0);
                const clauseAvg = scores.length ? avg(scores) : 0;
                const compRow = compMap[String(fid)];
                const scoringMode = localStorage.getItem('qoc_scoring_mode') || 'benchmark';
                const overall = compRow ? parseFloat(scoringMode === 'benchmark' ? (compRow.benchmark_percentage || compRow.average_percentage) : compRow.average_percentage) : clauseAvg;
                return { ...fac, clause_scores:clauseScores, overall_percentage:overall.toFixed(2) };
            }).filter(f=>Object.values(f.clause_scores).some(s=>s>0));
            // Build quality index map keyed by facility_id — filter to selected period
            const allQiItems = qiRes.data?.data || [];
            const qiItems    = allQiItems.filter(q => (q.period_label||'').trim() === period.trim());
            const qiMap      = Object.fromEntries(qiItems.map(q=>[String(q.facility_id), q]));
            // Attach quality index data to each facility
            const facilitiesWithQI = facilities.map(f => ({
                ...f,
                qi: qiMap[String(f.id)] || null,
            }));

            // Apply facility type filter
            const typeFacilities = facType === 'all' ? facilitiesWithQI :
                facilitiesWithQI.filter(f => (f.site_type||'').toLowerCase().includes(facType.toLowerCase()));

            if (!typeFacilities.length) { message.warning('No facilities found for the selected type'); setLoading(false); return; }

            // Keep the raw, un-aggregated assessment rows (one row per clause-audit,
            // i.e. Kobo "submission" granularity — assessment_id, assessor, date,
            // status are all only available at THIS level, not on the aggregated
            // per-facility clause_scores object). Needed for the Excel export's
            // submission-level sheet; restricted to facilities that survived the
            // facility-type filter, same scope as typeFacilities.
            const rawAssessments = regionFilteredData.filter(a =>
                typeFacilities.some(f => String(f.id) === String(a.facility_id))
            );

            // region stays 'all' for anything but an exact single-region selection,
            // so the rest of the app's existing region==='all' branching (which
            // decides National vs Regional report body, section applicability,
            // etc.) keeps working unchanged for the 2-or-3-regions case.
            const regionField = isSingleRegionSelection ? selectedRegions[0] : 'all';
            setData({ region: regionField, selectedRegionsSnapshot: [...selectedRegions], period_label:period, facilities: typeFacilities, meResponses:{}, qiItems, facType, rawAssessments });
            message.success(`Loaded ${facilities.length} facilities`);

            // Fresh data means a fresh default selection — everything included.
            setEnabledFacilityIds(new Set(typeFacilities.map(f=>String(f.id))));
            setEnabledClauses(new Set(CLAUSES.map(c=>c.code)));
            setEnabledSections(new Set(SECTION_DEFS.map(s=>s.id)));
            setQiOverride(null);
            setComparisonPeriod(null);
            setComparisonScores(null);

            // Background ME enrichment
            setMeLoading(true);
            setTimeout(async () => {
                try {
                    const allAsmtIds = [...new Set(regionFilteredData.filter(a => typeFacilities.some(f=>String(f.id)===String(a.facility_id))).map(a=>a.id))];
                    const BATCH = 15;
                    const meResponses = {};
                    for (let i=0; i<allAsmtIds.length; i+=BATCH) {
                        const batch = allAsmtIds.slice(i, i+BATCH);
                        const results = await Promise.all(
                            batch.map(id => QoCService.getAssessmentResponsesME(id)
                                .then(r => {
                                    const asmt = regionFilteredData.find(a=>a.id===id);
                                    const fac  = facMap[String(asmt?.facility_id)] || {};
                                    return (r.data?.data||[]).map(me=>({
                                        ...me,
                                        business_name: fac.business_name||'',
                                        site_type:     fac.site_type||'',
                                    }));
                                })
                                .catch(()=>[])
                            )
                        );
                        results.flat().forEach(me => {
                            if (!me.clause_code) return;
                            if (!meResponses[me.clause_code]) meResponses[me.clause_code] = [];
                            meResponses[me.clause_code].push(me);
                        });
                    }
                    setData(prev => prev ? { ...prev, meResponses } : prev);
                    message.success('Narrative enrichment complete');
                } catch(e) {
                    console.warn('ME enrichment failed:', e.message);
                } finally {
                    setMeLoading(false);
                }
            }, 100);

        } catch(e) { message.error('Failed: '+e.message); }
        finally { setLoading(false); }
    };

    // ── Derived, filtered report data ───────────────────────────────────────
    // Facility and clause selection are applied HERE, once, rather than inside
    // NationalReport/RegionalReport — that way every calculation downstream
    // (averages, star ratings, best/worst lists, quality index tables, etc.)
    // is automatically consistent with what's selected: a deselected facility
    // is removed everywhere, not just hidden from one list, and a deselected
    // clause is excluded from a facility's own average score, not just from
    // one table. Section visibility (enabledSections) is handled separately,
    // inside the report bodies, since it's purely a "show/hide this block"
    // concern rather than something that changes the underlying numbers.
    const filteredFacilities = useMemo(() => {
        if (!data) return [];
        return data.facilities
            .filter(f => enabledFacilityIds.has(String(f.id)))
            .map(f => {
                const scores = CLAUSES
                    .filter(c => enabledClauses.has(c.code))
                    .map(c => parseFloat(f.clause_scores?.[c.code]||0))
                    .filter(s => s > 0);
                const overall = scores.length ? avg(scores) : 0;
                // Quality Index (qoc_score / survey_composite / quality_index) is a
                // weighted formula the client can't safely reproduce, so rather than
                // approximate it we swap in the backend's freshly recalculated qi
                // object (see the debounced effect below) once it's available.
                const qi = (qiOverride && qiOverride[String(f.id)]) || f.qi;
                return { ...f, overall_percentage: overall.toFixed(2), qi };
            });
    }, [data, enabledFacilityIds, enabledClauses, qiOverride]);

    const filteredData = useMemo(() => {
        if (!data) return null;
        const visibleNames = new Set(filteredFacilities.map(f => f.business_name));
        const visibleIds   = new Set(filteredFacilities.map(f => String(f.id)));
        const filteredMe = {};
        Object.entries(data.meResponses || {}).forEach(([code, arr]) => {
            if (!enabledClauses.has(code)) return;
            const kept = arr.filter(m => visibleNames.has((m.business_name||'').trim()));
            if (kept.length) filteredMe[code] = kept;
        });
        return {
            ...data,
            facilities: filteredFacilities,
            meResponses: filteredMe,
            qiItems: (data.qiItems||[]).filter(q => visibleIds.has(String(q.facility_id))),
        };
    }, [data, filteredFacilities, enabledClauses]);

    // Raw per-clause-assessment rows, filtered to the same facility/clause
    // selection as everything else in the report. This is what the Excel
    // export's submission-level sheet is built from — filtering it here means
    // "export" always matches what's currently on screen, not the full
    // unfiltered load.
    const filteredRawAssessments = useMemo(() => {
        if (!data || !data.rawAssessments) return [];
        const visibleIds = new Set(filteredFacilities.map(f => String(f.id)));
        return data.rawAssessments.filter(a =>
            visibleIds.has(String(a.facility_id)) && (!a.clause_code || enabledClauses.has(a.clause_code))
        );
    }, [data, filteredFacilities, enabledClauses]);

    // Regions actually present in the LIVE, customized facility set — not the
    // load-time selection. If the user deselects every facility in a region
    // via the Customize drawer, that region should drop out of this list
    // immediately, same as it drops out of regionStats inside the report body.
    const liveRegions = data
        ? REGION_LIST.filter(r => filteredFacilities.some(f => (f.located_region||f.region||'').toLowerCase().includes(r.toLowerCase())))
        : [];

    // ── Server-side Quality Index recalculation ─────────────────────────────
    // Debounced so rapid checkbox toggling in the customize panel doesn't fire
    // a request per click. Sends facility_id/clause_code as comma-separated
    // strings — the backend normalizer accepts that form as well as arrays,
    // and it sidesteps any ambiguity in how axios serializes array params.
    useEffect(() => {
        if (!data) return;
        // Nothing selected in either dimension → nothing meaningful to recalc.
        if (enabledFacilityIds.size === 0 || enabledClauses.size === 0) return;

        const isFullSelection =
            enabledFacilityIds.size === data.facilities.length &&
            enabledClauses.size === CLAUSES.length;
        if (isFullSelection) {
            // Back to the default — just use the originally loaded qi data,
            // no need to ask the backend to recompute what it already gave us.
            setQiOverride(null);
            return;
        }

        const timer = setTimeout(() => {
            setQiRecalculating(true);
            QoCService.getQualityIndex({
                period_label: data.period_label,
                facility_id: Array.from(enabledFacilityIds).join(','),
                clause_code: Array.from(enabledClauses).join(','),
            }).then(res => {
                const rows = res.data?.data || [];
                const map = {};
                rows.forEach(r => { map[String(r.facility_id)] = r; });
                setQiOverride(map);
            }).catch(() => {
                message.warning('Could not recalculate Quality Index for this selection — showing unfiltered figures.');
            }).finally(() => {
                setQiRecalculating(false);
            });
        }, 400);

        return () => clearTimeout(timer);
    }, [data, enabledFacilityIds, enabledClauses]);

    // ── Period Comparison fetch ──────────────────────────────────────────────
    // Fetches clause scores for the user-picked comparison period, filtered
    // server-side to the SAME facility_id/clause_code selection as the
    // current period — so the resulting chart compares like-for-like scope,
    // not the comparison period's full unfiltered dataset. Debounced for the
    // same reason as the QI recalc above: rapid checkbox toggling shouldn't
    // fire a request per click.
    useEffect(() => {
        if (!data || !comparisonPeriod) { setComparisonScores(null); return; }

        const timer = setTimeout(() => {
            setComparisonLoading(true);
            QoCService.getClauseScoresAcrossFacilities({
                period_label: comparisonPeriod,
                facility_id: Array.from(enabledFacilityIds).join(','),
                clause_code: Array.from(enabledClauses).join(','),
            }).then(res => {
                const rows = res.data?.data || [];
                const sums = {}; const counts = {};
                rows.forEach(r => {
                    const code = r.clause_code;
                    if (!code) return;
                    const v = parseFloat(r.avg_score ?? r.total_score ?? 0);
                    if (!v) return;
                    sums[code]   = (sums[code]||0) + v;
                    counts[code] = (counts[code]||0) + 1;
                });
                const out = {};
                Object.keys(sums).forEach(code => { out[code] = sums[code] / counts[code]; });
                setComparisonScores(out);
            }).catch(() => {
                message.warning('Could not load comparison period data.');
                setComparisonScores(null);
            }).finally(() => {
                setComparisonLoading(false);
            });
        }, 400);

        return () => clearTimeout(timer);
    }, [data, comparisonPeriod, enabledFacilityIds, enabledClauses]);

    // ── Customize-panel helpers ─────────────────────────────────────────────
    const toggleSection = (id) => setEnabledSections(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const toggleClause = (code) => setEnabledClauses(prev => {
        const next = new Set(prev);
        next.has(code) ? next.delete(code) : next.add(code);
        return next;
    });
    const toggleFacility = (id) => setEnabledFacilityIds(prev => {
        const next = new Set(prev);
        const key = String(id);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });

    const applicableSectionDefs = SECTION_DEFS.filter(s => s.appliesTo === 'both' || s.appliesTo === (!data || data.region === 'all' ? 'national' : 'regional'));

    // Whether the currently-loaded report deviates from "everything included"
    // — used to badge the report as Customized in the controls bar, the Modal
    // title, and the report's own cover page, so nobody mistakes a narrowed
    // view for the full dataset.
    const isCustomized = !!data && (
        enabledFacilityIds.size !== data.facilities.length ||
        enabledClauses.size !== CLAUSES.length ||
        !applicableSectionDefs.every(s => enabledSections.has(s.id)) ||
        (data.selectedRegionsSnapshot && data.selectedRegionsSnapshot.length < REGION_LIST.length) ||
        (data.facType && data.facType !== 'all')
    );

    const setAllSections = (on) => setEnabledSections(prev => {
        const next = new Set(prev);
        applicableSectionDefs.forEach(s => on ? next.add(s.id) : next.delete(s.id));
        return next;
    });
    const setAllClauses = (on) => setEnabledClauses(on ? new Set(CLAUSES.map(c=>c.code)) : new Set());
    const setAllFacilities = (on) => setEnabledFacilityIds(on && data ? new Set(data.facilities.map(f=>String(f.id))) : new Set());

    const facilitiesForPanel = data
        ? data.facilities.filter(f => (f.business_name||'').toLowerCase().includes(facilitySearch.toLowerCase()))
        : [];
    const facilitiesByRegion = (data && data.region === 'all')
        ? REGION_LIST.reduce((acc, r) => {
            acc[r] = facilitiesForPanel.filter(f => (f.located_region||f.region||'').toLowerCase().includes(r.toLowerCase()));
            return acc;
        }, {})
        : null;

    // ── PRINT (fixed) ────────────────────────────────────────────────────────
    // Fixes applied vs. original:
    //  1. getElementById now targets 'qoc-report-root' (the actual report content),
    //     not the Modal's scroll-container id, which was ambiguous/duplicated.
    //  2. Removed the print rule `table{page-break-inside:avoid}`. That rule forced
    //     entire large tables (the national/regional heatmap, the Quality Index
    //     tables) to stay on one page; since those tables are routinely taller than
    //     one printed page, the print engine pushed the WHOLE table to a new page,
    //     leaving the remainder of the previous page blank. Rows still avoid
    //     splitting (`tr{page-break-inside:avoid}` is kept) so no row is cut in half.
    //  3. Added `thead{display:table-header-group}` so that if a table does span
    //     multiple printed pages, the header row repeats at the top of each page.
    const handlePrint = () => {
        const el = document.getElementById('qoc-report-root');
        if (!el) { message.error('Report not found'); return; }
        if (meLoading) {
            message.warning('Narratives are still enriching — please wait a moment before printing so those sections aren\'t skipped.');
            return;
        }

        // Expand collapsed sections
        const allDivs = el.querySelectorAll('[style]');
        const saved = [];
        allDivs.forEach(div => {
            if (div.style.maxHeight || div.style.opacity === '0') {
                saved.push({ el:div, mh:div.style.maxHeight, op:div.style.opacity, ov:div.style.overflow });
                if (div.style.maxHeight) div.style.maxHeight = 'none';
                if (div.style.opacity === '0') div.style.opacity = '1';
                if (div.style.overflow === 'hidden') div.style.overflow = 'visible';
            }
        });

        // Swap canvas to images
        const canvases = Array.from(el.querySelectorAll('canvas'));
        const restored = [];
        canvases.forEach(canvas => {
            try {
                if (canvas.width > 0 && canvas.height > 0) {
                    const img = document.createElement('img');
                    img.src = canvas.toDataURL('image/png');
                    img.style.cssText = `width:${canvas.offsetWidth}px;height:${canvas.offsetHeight}px;display:block;max-width:100%;`;
                    canvas.parentNode.replaceChild(img, canvas);
                    restored.push({ img, canvas });
                }
            } catch(e) {}
        });

        // Get full HTML snapshot
        const snapshot = el.innerHTML;

        // Restore immediately
        saved.forEach(s => {
            s.el.style.maxHeight = s.mh;
            s.el.style.opacity   = s.op;
            s.el.style.overflow  = s.ov;
        });
        restored.forEach(({ img, canvas }) => {
            try { img.parentNode.replaceChild(canvas, img); } catch(e) {}
        });

        // Pull in the REAL app stylesheets (antd + any global CSS) so Row/Col grid,
        // Progress, Tag, Divider etc. render the same way they do in the browser preview.
        // Without this, the new window has none of that CSS and layout breaks.
        const styleTags = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
            .map(node => node.tagName === 'LINK'
                ? `<link rel="stylesheet" href="${node.href}">`   // node.href is the resolved absolute URL
                : `<style>${node.textContent}</style>`)
            .join('\n');

        // Open new window - simple clean approach
        const win = window.open('', '_blank');
        win.document.write('<!DOCTYPE html><html><head><meta charset="utf-8"/><title>QoC Report</title>' +
            styleTags +
            '<style>' +
            '*{box-sizing:border-box;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}' +
            'body{margin:16px;font-family:Arial,sans-serif;font-size:11px;background:#f4f6fb;color:#222;}' +
            'img{max-width:100%!important;height:auto!important;}' +
            '.report-page{page-break-before:always;break-before:page;min-height:0!important;}' +
            '.report-page:first-child{page-break-before:avoid;break-before:avoid;}' +
            '.report-section{page-break-before:always;break-before:page;}' +
            'thead{display:table-header-group;}' +
            'tr{page-break-inside:avoid;break-inside:avoid;}' +
            '@page{size:A4 portrait;margin:8mm;}' +
            '@media print{ *{max-height:none!important;overflow:visible!important;opacity:1!important;} }' +
            '</style></head><body>' +
            '<div style="background:#f4f6fb;">' + snapshot + '</div>' +
            '<script>window.onload=function(){setTimeout(function(){window.print();},600);};<\/script>' +
            '</body></html>');
        win.document.close();
    };

    // ── EXPORT EXCEL (replaces the old Word export) ──────────────────────────
    // Structured as a KoboCollect-style dump rather than a rendered document:
    //   • Submissions  — one row per clause-assessment (Kobo "submission"
    //     granularity). This is the only place assessment_id, assessor, date,
    //     and status exist — they get lost once assessments are aggregated
    //     into a facility's clause_scores object, so this sheet is built from
    //     filteredRawAssessments, not from filteredFacilities.
    //   • Measurable_Elements — one row per ME response, linked back to
    //     Submissions via assessment_id (Kobo's repeat-group convention: a
    //     child sheet linked to its parent by an id column).
    //   • Facility_Summary — one row per facility with the clause-score
    //     matrix as columns, for anyone who just wants a quick pivot without
    //     reconstructing it from the other two sheets.
    // All three sheets reflect whatever is currently selected in the
    // Customize drawer, same as every other part of the report.
    const generateExcel = async () => {
        if (!data) return;
        if (meLoading) {
            message.warning('Narratives are still enriching — please wait a moment before exporting so the ME sheet isn\'t incomplete.');
            return;
        }
        try {
            message.loading({ content: 'Building Excel export…', key: 'excelgen', duration: 0 });

            const XLSX = await import('xlsx');

            const region = data.region === 'all' ? 'National' : data.region;
            const period = data.period_label || '';
            const facMapById = Object.fromEntries(filteredFacilities.map(f => [String(f.id), f]));

            // ── Sheet 1: Submissions (one row per clause-assessment) ──────────
            const submissionsRows = filteredRawAssessments.map(a => {
                const fac = facMapById[String(a.facility_id)] || {};
                return {
                    assessment_id:    a.id,
                    facility_id:      a.facility_id,
                    facility_name:    a.facility_name || fac.business_name || '',
                    region:           fac.located_region || fac.region || '',
                    site_type:        a.site_type || fac.site_type || '',
                    clause_code:      a.clause_code || '',
                    clause_title:     a.clause_title || '',
                    period_label:     a.period_label || period,
                    assessment_date:  a.assessment_date || '',
                    assessor_name:    a.assessor_name || '',
                    status:           a.status || '',
                    overall_percentage: a.overall_percentage != null ? parseFloat(a.overall_percentage) : '',
                };
            });

            // ── Sheet 2: Measurable_Elements (child, linked by assessment_id) ──
            const meRows = [];
            Object.entries(data.meResponses || {}).forEach(([clauseCode, arr]) => {
                if (!enabledClauses.has(clauseCode)) return;
                arr.forEach(me => {
                    // Only include ME rows belonging to a facility still in scope —
                    // matches business_name since that's the join key the ME
                    // enrichment step tagged each row with.
                    const stillVisible = filteredFacilities.some(f => f.business_name === me.business_name);
                    if (!stillVisible) return;
                    meRows.push({
                        assessment_id:              me.assessment_id ?? '',
                        facility_name:              me.business_name || '',
                        clause_code:                clauseCode,
                        me_id:                      me.me_id ?? '',
                        ref_no:                     me.ref_no || '',
                        me_text:                    me.me_text || '',
                        fulfillment:                me.fulfillment || '',
                        comment:                    me.comment || '',
                        general_observation:        me.general_observation || '',
                        criticality_of_observation: me.criticality_of_observation || '',
                        area_of_good_practice:      me.area_of_good_practice || '',
                        evidence_notes:             me.evidence_notes || '',
                    });
                });
            });

            // ── Sheet 3: Facility_Summary (pre-aggregated, one row per facility) ─
            const activeClauseList = CLAUSES.filter(c => enabledClauses.has(c.code));
            const summaryRows = filteredFacilities.map(f => {
                const row = {
                    facility_id:   f.id,
                    facility_name: f.business_name,
                    region:        f.located_region || f.region || '',
                    site_type:     f.site_type || '',
                    overall_percentage: f.overall_percentage != null ? getFacScore(f) : '',
                };
                activeClauseList.forEach(c => {
                    const v = f.clause_scores?.[c.code];
                    row[c.code] = v != null && parseFloat(v) > 0 ? parseFloat(v) : '';
                });
                if (f.qi) {
                    row.qoc_score        = f.qi.qoc_score != null ? parseFloat(f.qi.qoc_score) : '';
                    row.survey_composite = f.qi.survey_composite != null ? parseFloat(f.qi.survey_composite) : '';
                    row.quality_index    = f.qi.quality_index != null ? parseFloat(f.qi.quality_index) : '';
                }
                return row;
            });

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(submissionsRows), 'Submissions');
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(meRows), 'Measurable_Elements');
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'Facility_Summary');

            const filename = `QoC_Data_${region.replace(/\s+/g,'_')}_${period.replace(/[^A-Za-z0-9]/g,'_')}.xlsx`;
            XLSX.writeFile(wb, filename);

            message.success({ content: `Excel exported — ${submissionsRows.length} submissions, ${meRows.length} ME responses, ${summaryRows.length} facilities`, key: 'excelgen' });

        } catch(err) {
            console.error(err);
            message.error({ content: 'Excel export failed: ' + err.message, key: 'excelgen' });
        }
    };

    return (
        <>
        <style>{`
            @media print {
                * { max-height: none !important; overflow: visible !important; opacity: 1 !important; }
                .report-page { page-break-before: always; page-break-inside: avoid; margin-bottom: 0 !important; display: block !important; }
                .report-page:first-child { page-break-before: auto; }
                .report-section { page-break-before: always; margin-bottom: 4px !important; display: block !important; }
            }
        `}</style>
        <Modal open onCancel={onClose} footer={null} width={1100} style={{ top:10 }}
            bodyStyle={{ padding:0 }}
            title={<span style={{ fontWeight:800 }}>Regional & National QoC Report{isCustomized && <span style={{ color:'#d46b08', fontWeight:700 }}>Customized</span>}</span>}>
            {/* Controls bar */}
            <div style={{ padding:'12px 16px', background:'#f8faff', borderBottom:'1px solid #e0eaff', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                <Select value={period} onChange={setPeriod} style={{ minWidth:180 }} placeholder="Select Period">
                    {periods.filter(p=>!p.label.startsWith('ENHEA')).map(p=>(
                        <Option key={p.label} value={p.label}>{p.is_current==1?'★ ':''}{p.label}</Option>
                    ))}
                </Select>
                <Select
                    mode="multiple"
                    value={selectedRegions}
                    onChange={(vals) => setSelectedRegions(vals.length ? vals : REGION_LIST)}
                    style={{ minWidth:220 }}
                    placeholder="Select Regions"
                    maxTagCount={2}
                >
                    {REGION_LIST.map(r=><Option key={r} value={r}>{r} Region</Option>)}
                </Select>
                <Select value={facType} onChange={setFacType} style={{ minWidth:180 }}>
                    <Option value="all">All Facility Types</Option>
                    <Option value="hospital">Hospitals</Option>
                    <Option value="health centre">Health Centres</Option>
                    <Option value="clinic">Clinics & PHUs</Option>
                </Select>
                <Button type="primary" loading={loading} onClick={load}
                    style={{ background:'#1f3864', borderColor:'#1f3864' }}>
                    Generate Report
                </Button>
                {data && <Button icon={<FilePdfOutlined/>} disabled={meLoading} onClick={handlePrint} style={{ background:meLoading?undefined:'#cf1322', borderColor:meLoading?undefined:'#cf1322', color:meLoading?undefined:'#fff' }}>Save PDF</Button>}
                {data && <Button icon={<FileExcelOutlined/>} disabled={meLoading} onClick={generateExcel} style={{ background:meLoading?undefined:'#217346', borderColor:meLoading?undefined:'#217346', color:meLoading?undefined:'#fff' }}>Export Excel</Button>}
                {data && <Button icon={<SettingOutlined/>} onClick={()=>setCustomizeOpen(o=>!o)}>Customize Report</Button>}
                {data && <Tag color="green">{filteredFacilities.length} of {data.facilities.length} facilities · {data.region!=='all' ? data.region : (liveRegions.length < REGION_LIST.length ? liveRegions.join(', ') : 'National')}{data.facType&&data.facType!=='all'?' · '+data.facType:''}</Tag>}
                {isCustomized && <Tag color="orange">🛠 Customized</Tag>}
                {meLoading && <Tag color="orange">Enriching narratives</Tag>}
                {qiRecalculating && <Tag color="blue">Recalculating Quality Index</Tag>}
                {comparisonLoading && <Tag color="blue">Loading comparison period</Tag>}
            </div>

            {/* Report content — note: this outer div keeps id="qoc-regional-report" for
                the Modal's scroll container. The inner report roots (NationalReport /
                RegionalReport) now use the distinct id="qoc-report-root", which is what
                handlePrint targets. Previously both used the same id, which was
                ambiguous and fragile. */}
            <div id="qoc-regional-report" style={{ height:'75vh', overflowY:'auto', background:'#f4f6fb', padding:'0 0 40px' }}>
                {!data && !loading && (
                    <div style={{ textAlign:'center', padding:'60px 20px', color:'#888' }}>
                        <div style={{ fontSize:56, marginBottom:16 }}></div>
                        <div style={{ fontSize:20, fontWeight:600 }}>Select a period and click Generate Report</div>
                    </div>
                )}
                {loading && (
                    <div style={{ textAlign:'center', padding:'60px 20px' }}>
                        <Spin size="large"/>
                        <div style={{ marginTop:16, color:'#888' }}>Loading assessment data…</div>
                    </div>
                )}
                {filteredData && (
                    filteredData.region === 'all'
                        ? <NationalReport data={filteredData} meResponses={filteredData.meResponses} enabledSections={enabledSections} enabledClauses={enabledClauses} isCustomized={isCustomized} comparisonPeriod={comparisonPeriod} comparisonScores={comparisonScores}/>
                        : <RegionalReport data={filteredData} meResponses={filteredData.meResponses} enabledSections={enabledSections} enabledClauses={enabledClauses} isCustomized={isCustomized} comparisonPeriod={comparisonPeriod} comparisonScores={comparisonScores}/>
                )}
            </div>
        </Modal>

        {/* Customize Report drawer — deliberately a SIBLING of Modal, not nested
            inside it. Nesting it inside Modal's JSX caused the two components'
            portals/z-index (both default to ~1000) to conflict, so clicks on
            the Drawer's own close (X) button were getting swallowed by
            Modal's stacking context instead of reaching Drawer's onClose.
            Sitting it alongside Modal, plus an explicit zIndex above Modal's,
            fixes that regardless of DOM mount order.
            Lets the user pick which sections appear, which of the 10 clauses
            are included, and which facilities are included. Facility and
            clause selections change the underlying numbers (see
            filteredFacilities/filteredData above); section selection only
            changes what's visible, not the calculations.

            Belt-and-suspenders close mechanism: some deployments have reported
            the header X not responding (root cause never fully pinned down —
            possibly a leftover portal from a Select dropdown elsewhere on the
            page intercepting clicks at that exact spot). Rather than keep
            chasing that, there are now THREE independent ways to close this:
            the header X (antd default), a full-width "Close" button at the
            very top of the body content, and the Escape key (explicit
            listener below, not just relying on antd's built-in handling). */}
        <Drawer
            title="Customize Report"
            open={customizeOpen}
            onClose={()=>setCustomizeOpen(false)}
            width={420}
            zIndex={1050}
            maskClosable={true}
            keyboard={true}
        >
            {!data ? (
                <Empty description="Generate a report first"/>
            ) : (
                <>
                    <Button
                        block
                        onClick={()=>setCustomizeOpen(false)}
                        style={{ marginBottom:20, fontWeight:700, background:'#1f3864', color:'#fff', borderColor:'#1f3864' }}
                    >
                        ✕ Close
                    </Button>

                    {/* Sections */}
                    <div style={{ marginBottom:24 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                            <div style={{ fontWeight:700, color:'#1f3864' }}>Report Sections</div>
                            <div style={{ display:'flex', gap:10 }}>
                                <a onClick={()=>setAllSections(true)}>All</a>
                                <a onClick={()=>setAllSections(false)}>None</a>
                            </div>
                        </div>
                        {applicableSectionDefs.map(s => (
                                <div key={s.id} style={{ marginBottom:6 }}>
                                    <Checkbox checked={enabledSections.has(s.id)} onChange={()=>toggleSection(s.id)}>{s.label}</Checkbox>
                                </div>
                            ))}
                        </div>

                        <Divider/>

                        {/* Clauses */}
                        <div style={{ marginBottom:24 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                                <div style={{ fontWeight:700, color:'#1f3864' }}>Clauses</div>
                                <div style={{ display:'flex', gap:10 }}>
                                    <a onClick={()=>setAllClauses(true)}>All</a>
                                    <a onClick={()=>setAllClauses(false)}>None</a>
                                </div>
                            </div>
                            {CLAUSES.map(c => (
                                <div key={c.code} style={{ marginBottom:6 }}>
                                    <Checkbox checked={enabledClauses.has(c.code)} onChange={()=>toggleClause(c.code)}>
                                        <span style={{ fontWeight:700, color:c.color, marginRight:6 }}>{c.code}</span>
                                        <span style={{ fontSize:16, color:'#555' }}>{c.title}</span>
                                    </Checkbox>
                                </div>
                            ))}
                        </div>

                        <Divider/>

                        {/* Period Comparison */}
                        <div style={{ marginBottom:24 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                                <div style={{ fontWeight:700, color:'#1f3864' }}>Period Comparison</div>
                                {comparisonPeriod && <a onClick={()=>setComparisonPeriod(null)}>Clear</a>}
                            </div>
                            <div style={{ fontSize:15, color:'#888', marginBottom:8 }}>
                                Compare {data.period_label} against another period, using the same facility/clause selection above.
                            </div>
                            <Select
                                allowClear
                                style={{ width:'100%' }}
                                placeholder="Select a period to compare against"
                                value={comparisonPeriod}
                                onChange={setComparisonPeriod}
                            >
                                {periods.filter(p => !p.label.startsWith('ENHEA') && p.label !== data.period_label).map(p => (
                                    <Option key={p.label} value={p.label}>{p.is_current==1?'★ ':''}{p.label}</Option>
                                ))}
                            </Select>
                            {comparisonLoading && <div style={{ fontSize:15, color:'#1890ff', marginTop:6 }}>Loading comparison data</div>}
                        </div>

                        <Divider/>

                        {/* Facilities */}
                        <div>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                                <div style={{ fontWeight:700, color:'#1f3864' }}>
                                    Facilities ({enabledFacilityIds.size}/{data.facilities.length})
                                </div>
                                <div style={{ display:'flex', gap:10 }}>
                                    <a onClick={()=>setAllFacilities(true)}>All</a>
                                    <a onClick={()=>setAllFacilities(false)}>None</a>
                                </div>
                            </div>
                            <Input
                                placeholder="Search facilities..."
                                prefix={<SearchOutlined/>}
                                value={facilitySearch}
                                onChange={e=>setFacilitySearch(e.target.value)}
                                style={{ marginBottom:10 }}
                                allowClear
                            />
                            <div style={{ maxHeight:340, overflowY:'auto', border:'1px solid #f0f0f0', borderRadius:6, padding:8 }}>
                                {(data && data.region === 'all') ? (
                                    REGION_LIST.map(r => {
                                        const facs = facilitiesByRegion[r] || [];
                                        if (!facs.length) return null;
                                        const allOn  = facs.every(f=>enabledFacilityIds.has(String(f.id)));
                                        const someOn = facs.some(f=>enabledFacilityIds.has(String(f.id)));
                                        return (
                                            <div key={r} style={{ marginBottom:12 }}>
                                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                                                    <span style={{ fontWeight:700, fontSize:16, color:REGION_COLORS[r] }}>{r} ({facs.length})</span>
                                                    <Checkbox
                                                        checked={allOn}
                                                        indeterminate={!allOn && someOn}
                                                        onChange={e => {
                                                            const on = e.target.checked;
                                                            setEnabledFacilityIds(prev => {
                                                                const next = new Set(prev);
                                                                facs.forEach(f => on ? next.add(String(f.id)) : next.delete(String(f.id)));
                                                                return next;
                                                            });
                                                        }}
                                                    />
                                                </div>
                                                {facs.map(f => (
                                                    <div key={f.id} style={{ marginLeft:12, marginBottom:3 }}>
                                                        <Checkbox checked={enabledFacilityIds.has(String(f.id))} onChange={()=>toggleFacility(f.id)}>
                                                            <span style={{ fontSize:16 }}>{f.business_name}</span>
                                                        </Checkbox>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })
                                ) : (
                                    facilitiesForPanel.map(f => (
                                        <div key={f.id} style={{ marginBottom:4 }}>
                                            <Checkbox checked={enabledFacilityIds.has(String(f.id))} onChange={()=>toggleFacility(f.id)}>
                                                <span style={{ fontSize:16 }}>{f.business_name}</span>
                                            </Checkbox>
                                        </div>
                                    ))
                                )}
                                {facilitiesForPanel.length === 0 && <Empty description="No facilities match"/>}
                            </div>
                        </div>
                    </>
                )}
            </Drawer>
        </>
    );
}